// ============================================================================
// UNIFIED OVERVIEW TAB
// Static layout for all work item types - replaces dynamic form configurations
// Shows: Title, Description, Contact, Location, Contact Person, Progress, Tags
// Date: December 12, 2025
// Updated: December 21, 2025 - Added 3-way search (Contact, Location, Person)
// Updated: December 22, 2025 - Replaced Schedule section with Progress & Effort tracker
// ============================================================================

'use client';

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  FileText, Calendar, Clock, User, Users, MapPin, Tag,
  Building, Phone, Mail, TrendingUp, Loader2, X, ChevronDown, Search, Building2,
  Target, Zap, PlayCircle, Briefcase, CalendarClock, ArrowRight, Lock, Info, Navigation,
  Wrench, Shield, ShieldCheck, ShieldAlert, Hash, Factory, AlertTriangle,
  Plus, CheckCircle2, Circle, GripVertical, Trash2, Edit2, ExternalLink, Copy, MessageCircle,
  ChevronRight, Flag
} from 'lucide-react';
import type { WorkItem } from '../UniversalWorkItems';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { fetchContactPeople } from '@/lib/contacts/fetchContactPeople';
import { notify, confirm } from '@/lib/notifications';
import { useWorkItemDetailContext, type ContactLocationDetail, type ContactSummary } from '@/contexts/WorkItemDetailContext';
import LocationAutocomplete, { CustomLocationResult } from '@/components/maps/LocationAutocomplete';
import type { CustomLocationData } from '@/components/maps/LocationAutocomplete';

import UnifiedContactLookup, {
  type UnifiedLookupContact,
  type UnifiedLookupContactPerson,
  type UnifiedLookupLocation,
  type UnifiedLookupSearchResult,
} from '@/components/shared/UnifiedContactLookup';
import PurchaseItemList from '@/components/work-items/PurchaseItemList';
import { normalizePurchaseListItems, type PurchaseListItem } from '@/components/work-items/purchaseListUtils';
import BrandedLoadingSpinner from '@/components/shared/BrandedLoadingSpinner';
import { DEFAULT_APPOINTMENT_TYPES, useJobConfig } from '@/hooks/useJobConfig';
import type { WorkItemOverviewInitialData } from './detailBundleHydration';
import { wsClient } from '@/lib/websocket';
import { buildOperationsRealtimeEvent } from '@/lib/websocket/operationsRealtime';
import {
  getEstimatedHoursForPoints,
  getEffortScaleEntry,
  getEffectiveEstimatedHours,
  calculateEstimatedHoursFromSchedule,
} from '@/lib/work-items/effortConfig';
import {
  deriveWorkItemProgress,
  isOperationalHierarchyItemType,
  type WorkItemProgressContext,
} from '@/lib/work-items/progress';
import {
  isTerminalWorkItemStatus,
  normalizeWorkflowStatusKey,
} from '@/lib/work-items/operationalStatusConfig';
import {
  PURCHASE_APPOINTMENT_TYPE_KEY,
  getAppointmentTypeRules,
  isInheritedParentJobCompletionOverrideReason,
  normalizeAppointmentTypeKey,
  type AppointmentCompletionRequirementKey,
  type AppointmentDetailTabKey,
  type ConfigurableTypeOption,
} from '@/app/client-zone/modules/total-operations/components/smart-task/utils/appointmentTypeRules';
import { buildStakeholderEntityWorkItemId } from '@/lib/contacts/stakeholderWorkItemIdentity';
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  addMinutesToLocalDateTimeValue,
  DEFAULT_APPOINTMENT_DURATION_MINUTES,
  getAppointmentScheduleErrorMessage,
  normalizeAppointmentSchedule,
} from '@/lib/work-items/appointmentSchedule';
import AppointmentScheduler, { getDefaultAppointmentSchedule } from '@/components/work-items/AppointmentScheduler';
import { formatLocalISOSAST, formatDateTimeSAST, formatTimeSAST, getTimePartsSAST, getDateStrSAST } from '@/lib/sastTime';
import { extractCoordinates } from '@/lib/work-items/appointmentLocationUtils';
import {
  getWorkItemResolutionType,
  resolveParentJobIdFromAppointment,
  shouldOfferNewFollowUpAppointment,
} from '@/lib/work-items/appointmentRescheduleState';
import {
  calculateContactAccountStanding,
  formatContactAccountStatusLabel,
  formatContactPaymentTerms,
} from '@/lib/contacts/accountStanding';
import { WORK_ITEM_LIST_CHANGED_EVENT } from '@/lib/websocket/lifecycle';
import dynamic from 'next/dynamic';
const AppointmentLiveMap = dynamic(() => import('./AppointmentLiveMap'), { ssr: false });

const APPOINTMENT_COMPLETION_OVERRIDE_REASON_MIN_LENGTH = 12;

// Contact type
interface Contact {
  id: number;
  work_item_id?: string;
  company_name: string | null;
  display_name?: string | null;
  contact_person: string | null;
  email: string | null;
  phone: string | null;
  mobile?: string | null;
  website?: string | null;
  contact_type: string;
  account_status?: string | null;
  is_active?: boolean | null;
  payment_terms?: string | number | null;
  payment_terms_days?: string | number | null;
  credit_limit?: number | string | null;
  current_balance?: number | string | null;
  currency?: string | null;
}

// Location type
interface Location {
  id: number;
  work_item_id?: string;
  contact_id: number;
  contact_work_item_id?: string | null;
  location_name: string;
  address_line1: string | null;
  address_line2?: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  contact_person?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  is_primary: boolean;
}

// Contact Person type
interface ContactPerson {
  id: number;
  work_item_id?: string;
  contact_id: number;
  contact_work_item_id?: string | null;
  first_name: string;
  last_name: string;
  job_title?: string;
  department?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  direct_line?: string;
  is_primary?: boolean;
  default_location_work_item_id?: string | null;
}

interface ContactDetailsState {
  company?: Contact;
  people: ContactPerson[];
  location?: {
    id: number;
    work_item_id?: string;
    location_name: string;
    contact_person?: string | null;
    contact_email?: string | null;
    contact_phone?: string | null;
    address?: string | null;
  };
}

function normalizePositiveIntegerId(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) && value > 0 ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }

    const parsed = Number.parseInt(trimmed, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  return null;
}

function resolveStakeholderWorkItemId(
  kind: 'contact' | 'location' | 'person',
  entityId: unknown,
): string | null {
  const normalizedId = normalizePositiveIntegerId(entityId);
  return normalizedId ? buildStakeholderEntityWorkItemId(kind, normalizedId) : null;
}

function getFirstNonEmptyString(values: unknown[]): string | null {
  for (const value of values) {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) {
        return trimmed;
      }
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }

  return null;
}

function getFirstPositiveIntegerFromValues(values: unknown[]): number | null {
  for (const value of values) {
    const normalized = normalizePositiveIntegerId(value);
    if (normalized != null) {
      return normalized;
    }
  }

  return null;
}

function getReferenceCount(values: unknown[]): number {
  for (const value of values) {
    if (Array.isArray(value)) {
      return value.filter((entry) => normalizePositiveIntegerId(entry) != null || (typeof entry === 'string' && entry.trim().length > 0)).length;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) {
        continue;
      }

      if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            return parsed.filter((entry) => normalizePositiveIntegerId(entry) != null || (typeof entry === 'string' && entry.trim().length > 0)).length;
          }
        } catch {
          // ignore malformed JSON and fall through to comma-separated parsing
        }
      }

      return trimmed
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
        .length;
    }

    const singleValue = getFirstPositiveIntegerFromValues([value]);
    if (singleValue != null) {
      return 1;
    }
  }

  return 0;
}

function formatContextStatusLabel(value: string | null | undefined): string | null {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  return trimmed
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function userCanManageAppointmentCompletionOverride(currentUser: any): boolean {
  if (!currentUser || typeof currentUser !== 'object') {
    return false;
  }

  const candidateRoles = [
    currentUser.role,
    currentUser.permissions?.role,
    currentUser.permissions?.client_role,
    currentUser.activeClient?.role,
  ]
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.toLowerCase());

  if (candidateRoles.some((role) => ['root', 'owner', 'admin', 'employee_admin', 'client_owner'].includes(role))) {
    return true;
  }

  const permissionValues = [
    ...(Array.isArray(currentUser.permissions) ? currentUser.permissions : []),
    ...(Array.isArray(currentUser.permissions?.permissions) ? currentUser.permissions.permissions : []),
    ...(Array.isArray(currentUser.permissions?.scopes) ? currentUser.permissions.scopes : []),
  ]
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.toLowerCase());

  return permissionValues.some((permission) => permission.includes('appointment') || permission.includes('work_item'));
}

interface AppointmentCompletionOverrideFormState {
  enabled: boolean;
  reason: string;
  approvedAt?: string | null;
  approvedByName?: string | null;
}

function getAppointmentCompletionOverrideFormState(customFields: Record<string, unknown>): AppointmentCompletionOverrideFormState {
  const override = customFields.completion_rules_override as Record<string, unknown> | null | undefined;
  if (!override || typeof override !== 'object') {
    return {
      enabled: false,
      reason: '',
    };
  }

  return {
    enabled: Boolean(override.enabled),
    reason: String(override.reason || ''),
    approvedAt: override.approved_at as string | null | undefined,
    approvedByName: override.approved_by_name as string | null | undefined,
  };
}

function getAppointmentCompletionOverrideFormStateKey(state: AppointmentCompletionOverrideFormState): string {
  return `${state.enabled}-${state.reason}-${state.approvedAt || ''}-${state.approvedByName || ''}`;
}

// ============================================================================
// SUBTASK TYPES & CONSTANTS
// ============================================================================

interface Subtask {
  id: string;
  item_number: string;
  title: string;
  description?: string | null;
  status: string;
  priority: 'low' | 'medium' | 'high' | 'urgent' | 'critical';
  assigned_to?: number | null;
  assigned_to_name?: string | null;
  due_date?: string | null;
  progress_percentage: number;
  item_type: string;
  relationship_id: string;
}

const SUBTASK_STATUSES = [
  { value: 'open', label: 'Open', color: 'gray' },
  { value: 'in_progress', label: 'In Progress', color: 'blue' },
  { value: 'review', label: 'In Review', color: 'yellow' },
  { value: 'blocked', label: 'Blocked', color: 'red' },
  { value: 'resolved', label: 'Resolved', color: 'green' },
  { value: 'done', label: 'Resolved', color: 'green' },
  { value: 'completed', label: 'Resolved', color: 'green' },
  { value: 'closed', label: 'Closed', color: 'gray' },
  { value: 'cancelled', label: 'Cancelled', color: 'red' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'text-gray-500', icon: '↓' },
  { value: 'medium', label: 'Medium', color: 'text-blue-500', icon: '=' },
  { value: 'high', label: 'High', color: 'text-orange-500', icon: '↑' },
  { value: 'urgent', label: 'Urgent', color: 'text-red-500', icon: '↑↑' },
  { value: 'critical', label: 'Critical', color: 'text-red-700', icon: '⚠' },
];

const isSubtaskCompleted = (status: string): boolean => {
  return isTerminalWorkItemStatus(status);
};

const getStatusColor = (status: string): string => {
  const statusColors: Record<string, string> = {
    'open': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    'in_progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    'in-progress': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    'review': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    'resolved': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    'done': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    'completed': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    'closed': 'bg-gray-200 text-gray-700 dark:bg-gray-600 dark:text-gray-300',
    'cancelled': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    'blocked': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  };
  return statusColors[status] || statusColors['open'];
};

// ============================================================================
// SORTABLE SUBTASK COMPONENT (drag-and-drop)
// ============================================================================

interface SortableSubtaskProps {
  subtask: Subtask;
  isDark: boolean;
  onToggleStatus: (subtask: Subtask) => void;
  onRemove: (subtask: Subtask) => void;
  onOpenItem?: (itemId: string) => void;
  onOpenSubtask?: (itemId: string) => void;
}

const SortableSubtask = React.memo(function SortableSubtask({
  subtask, isDark, onToggleStatus, onRemove, onOpenItem, onOpenSubtask
}: SortableSubtaskProps) {
  const handleOpen = onOpenSubtask || onOpenItem;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: subtask.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 1
  };

  const statusConfig = SUBTASK_STATUSES.find(s => s.value === subtask.status);
  const priorityConfig = PRIORITY_OPTIONS.find(p => p.value === subtask.priority);
  const completed = isSubtaskCompleted(subtask.status);
  const isOverdue = subtask.due_date && new Date(subtask.due_date) < new Date() && !completed;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`group rounded-lg border transition-all ${isDragging ? 'shadow-xl' : ''
        } ${isDark
          ? 'border-gray-700 hover:border-gray-600 bg-gray-800/50 hover:bg-gray-800'
          : 'border-gray-200 hover:border-gray-300 bg-white hover:shadow-md'
        } ${completed ? 'opacity-60' : ''}`}
    >
      <div className="flex items-center gap-2 p-3">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="p-1 text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0"
        >
          <GripVertical className="w-4 h-4" />
        </button>

        {/* Checkbox */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleStatus(subtask);
          }}
          className="flex-shrink-0"
        >
          {completed ? (
            <CheckCircle2 className="w-5 h-5 text-green-500" />
          ) : (
            <Circle className={`w-5 h-5 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors`} />
          )}
        </button>

        {/* Content - clickable to open drawer */}
        <div
          className="flex-1 min-w-0 cursor-pointer"
          onClick={() => handleOpen?.(subtask.id)}
        >
          <div className="flex items-center gap-2">
            <span className={`text-sm font-medium text-left hover:text-blue-600 dark:hover:text-blue-400 transition-colors truncate ${completed ? 'line-through' : ''
              } ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              {subtask.title}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className={`text-[10px] font-mono ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              #{subtask.item_number}
            </span>
            {statusConfig && (
              <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] rounded font-medium ${getStatusColor(subtask.status)}`}>
                {statusConfig.label}
              </span>
            )}
            {priorityConfig && subtask.priority !== 'medium' && (
              <span className={`inline-flex items-center gap-0.5 text-[10px] font-medium ${priorityConfig.color}`}>
                {priorityConfig.icon} {priorityConfig.label}
              </span>
            )}
            {subtask.assigned_to_name && (
              <span className={`inline-flex items-center gap-1 text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <User className="w-2.5 h-2.5" />
                {subtask.assigned_to_name}
              </span>
            )}
            {subtask.due_date && (
              <span className={`inline-flex items-center gap-0.5 text-[10px] ${isOverdue
                ? 'text-red-500 font-semibold'
                : isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>
                <Calendar className="w-2.5 h-2.5" />
                {new Date(subtask.due_date).toLocaleDateString('en-ZA', { month: 'short', day: 'numeric' })}
                {isOverdue && ' !'}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-0.5 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          {handleOpen && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleOpen(subtask.id);
              }}
              className={`p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
              title="Open"
            >
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemove(subtask);
            }}
            className={`p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 ${isDark ? 'text-red-400' : 'text-red-500'}`}
            title="Remove"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// MAIN COMPONENT PROPS
// ============================================================================

export interface UnifiedOverviewTabProps {
  workItem: WorkItem;
  formData: Record<string, any>;
  onDataChange: (field: string, value: any) => void;
  isDark?: boolean;
  jobTypes?: ConfigurableTypeOption[];
  initialData?: WorkItemOverviewInitialData | null;
  initialDataLoading?: boolean;
  /** Pass these to enable the inline subtasks section */
  workItemId?: string;
  workItemType?: string;
  onOpenItem?: (itemId: string) => void;
  onOpenSubtask?: (itemId: string) => void;
  onSubtasksCountChange?: (count: number) => void;
}

const formatSubtypeLabel = (value: string) => value.replace(/[_-]/g, ' ').replace(/\b\w/g, char => char.toUpperCase());

const APPOINTMENT_REQUIREMENT_LABELS: Record<AppointmentCompletionRequirementKey, string> = {
  photos: 'Photos',
  signature: 'Signature',
  odometer: 'Odometer',
  expenses: 'Expense',
  public_comments: 'Public comments',
};

const APPOINTMENT_TAB_LABELS: Record<AppointmentDetailTabKey, string> = {
  'work-breakdown': 'Work Breakdown',
  photos: 'Photos',
  comments: 'Comments',
  materials: 'Parts',
};

const formatAppointmentRuleSummary = (values: string[], emptyLabel: string) => {
  return values.length > 0 ? values.join(', ') : emptyLabel;
};

type InitialOverviewLoadKey =
  | 'contacts'
  | 'timeEntries'
  | 'progress'
  | 'locations'
  | 'people'
  | 'contactDetails';

type InitialOverviewLoadState = Record<InitialOverviewLoadKey, boolean>;

const createInitialOverviewLoadState = ({
  hasContact,
  shouldLoadProgress,
  shouldLoadTimeEntries,
}: {
  hasContact: boolean;
  shouldLoadProgress: boolean;
  shouldLoadTimeEntries: boolean;
}): InitialOverviewLoadState => ({
  contacts: true,
  timeEntries: shouldLoadTimeEntries,
  progress: shouldLoadProgress,
  locations: hasContact,
  people: hasContact,
  contactDetails: hasContact,
});

function getAssigneeUserIdsFromWorkItem(w: WorkItem): number[] {
  const fromAssignees = (w.assignees || [])
    .map((a) => Number((a as { user_id?: number }).user_id))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (fromAssignees.length > 0) {
    return fromAssignees;
  }

  const raw = w.assigned_to;
  const first = Array.isArray(raw) ? raw[0] : raw;
  const n = typeof first === 'number' ? first : Number(first);
  return Number.isFinite(n) && n > 0 ? [n] : [];
}

export default function UnifiedOverviewTab({
  workItem,
  formData,
  onDataChange,
  isDark = false,
  jobTypes = [],
  initialData = null,
  initialDataLoading = false,
  workItemId,
  workItemType,
  onOpenItem,
  onOpenSubtask,
  onSubtasksCountChange,
}: UnifiedOverviewTabProps) {
  const detailContext = useWorkItemDetailContext();
  const contextLoadContacts = detailContext?.loadContacts;
  const contextLoadContactById = detailContext?.loadContactById;
  const contextLoadContactLocations = detailContext?.loadContactLocations;
  const contextLoadContactLocationById = detailContext?.loadContactLocationById;
  const contextLoadContactPeople = detailContext?.loadContactPeople;

  const resolvedContactId = useMemo(
    () => normalizePositiveIntegerId(formData.contact_id ?? (workItem as any).contact_id),
    [formData.contact_id, workItem],
  );

  const resolvedLocationId = useMemo(
    () => normalizePositiveIntegerId(formData.location_id ?? (workItem as any).location_id),
    [formData.location_id, workItem],
  );

  const resolvedContactPersonId = useMemo(
    () => normalizePositiveIntegerId(formData.contact_person_id ?? (workItem as any).contact_person_id),
    [formData.contact_person_id, workItem],
  );

  const resolvedContactPersonId2 = useMemo(
    () => normalizePositiveIntegerId(formData.contact_person_id2 ?? workItem.contact_person_id2),
    [formData.contact_person_id2, workItem],
  );

  const resolvedContactPersonId3 = useMemo(
    () => normalizePositiveIntegerId(formData.contact_person_id3 ?? workItem.contact_person_id3),
    [formData.contact_person_id3, workItem],
  );

  const resolvedContactPersonName = useMemo(() => {
    const rawValue = formData.contact_person ?? (workItem as any).contact_person ?? '';
    return typeof rawValue === 'string' ? rawValue.trim() : '';
  }, [formData.contact_person, workItem]);

  const hasPreloadedContacts = useMemo(() => {
    if (initialData?.contacts === undefined) {
      return false;
    }

    if (resolvedContactId == null) {
      return true;
    }

    return Array.isArray(initialData.contacts)
      && initialData.contacts.some((contact) => Number((contact as { id?: number | string }).id) === resolvedContactId);
  }, [initialData?.contacts, resolvedContactId]);

  const hasPreloadedLocations = useMemo(() => {
    if (initialData?.locations === undefined) {
      return false;
    }

    if (resolvedLocationId == null) {
      return true;
    }

    return Array.isArray(initialData.locations)
      && initialData.locations.some((location) => Number((location as { id?: number | string }).id) === resolvedLocationId);
  }, [initialData?.locations, resolvedLocationId]);

  const hasPreloadedContactPeople = useMemo(() => {
    if (initialData?.contactPeople === undefined) {
      return false;
    }

    if (resolvedContactPersonId == null && !resolvedContactPersonName) {
      return true;
    }

    return Array.isArray(initialData.contactPeople)
      && initialData.contactPeople.some((person) => {
        const personId = Number((person as { id?: number | string }).id);
        if (resolvedContactPersonId != null && personId === resolvedContactPersonId) {
          return true;
        }

        if (!resolvedContactPersonName) {
          return false;
        }

        const fullName = `${(person as { first_name?: string }).first_name || ''} ${(person as { last_name?: string }).last_name || ''}`.trim().toLowerCase();
        return fullName === resolvedContactPersonName.toLowerCase();
      });
  }, [initialData?.contactPeople, resolvedContactPersonId, resolvedContactPersonName]);

  const hasPreloadedContactDetails = useMemo(() => {
    if (initialData?.contactDetails === undefined) {
      return false;
    }

    if (resolvedContactId == null) {
      return true;
    }

    const preloadedCompanyId = Number((initialData.contactDetails as { company?: { id?: number | string } } | null)?.company?.id);
    if (preloadedCompanyId === resolvedContactId) {
      return true;
    }

    if (resolvedLocationId == null) {
      return false;
    }

    const preloadedLocationId = Number((initialData.contactDetails as { location?: { id?: number | string } } | null)?.location?.id);
    return preloadedCompanyId === resolvedContactId && preloadedLocationId === resolvedLocationId;
  }, [initialData?.contactDetails, resolvedContactId, resolvedLocationId]);

  const hasPreloadedTimeSummary = initialData?.timeSummary !== undefined;
  const hasPreloadedProgressChildren = initialData?.progressChildren !== undefined;
  const hasPreloadedSubtasks = initialData?.subtasks !== undefined;

  // Contacts state
  const [contacts, setContacts] = useState<Contact[]>(() => Array.isArray(initialData?.contacts) ? initialData.contacts as Contact[] : []);
  const [loadingContacts, setLoadingContacts] = useState(false);

  // Locations state
  const [locations, setLocations] = useState<Location[]>(() => Array.isArray(initialData?.locations) ? initialData.locations as Location[] : []);
  const [loadingLocations, setLoadingLocations] = useState(false);

  // Contact People state
  const [contactPeople, setContactPeople] = useState<ContactPerson[]>(() => Array.isArray(initialData?.contactPeople) ? initialData.contactPeople as ContactPerson[] : []);
  const [loadingPeople, setLoadingPeople] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState<ContactPerson | null>(null);
  const [selectedContactWorkItemId, setSelectedContactWorkItemId] = useState<string | null>(
    resolveStakeholderWorkItemId('contact', formData.contact_id ?? (workItem as any).contact_id),
  );
  const [selectedLocationWorkItemId, setSelectedLocationWorkItemId] = useState<string | null>(
    resolveStakeholderWorkItemId('location', formData.location_id ?? (workItem as any).location_id),
  );
  const [selectedContactPersonWorkItemId, setSelectedContactPersonWorkItemId] = useState<string | null>(
    resolveStakeholderWorkItemId('person', formData.contact_person_id ?? (workItem as any).contact_person_id),
  );
  const [contactDetails, setContactDetails] = useState<ContactDetailsState | null>(() => initialData?.contactDetails as ContactDetailsState | null ?? null);
  const [loadingContactDetails, setLoadingContactDetails] = useState(false);
  const activeContactIdRef = useRef<number | null>(null);
  const latestLocationLoadRequestId = useRef(0);
  const latestPeopleLoadRequestId = useRef(0);
  const lastLoadedContactRelationshipsRef = useRef<number | null>(null);
  const lastLoadedContactDetailsKeyRef = useRef<string>('');

  // Appointment location source: 'contact' | 'custom'
  const [locationSource, setLocationSource] = useState<'contact' | 'custom'>('contact');
  const [customLocation, setCustomLocation] = useState<CustomLocationData | null>(null);
  const [customLocSearch, setCustomLocSearch] = useState('');
  const [isEditingAppointmentContact, setIsEditingAppointmentContact] = useState(false);

  // Time entries state for actual hours calculation
  const [timeEntriesTotal, setTimeEntriesTotal] = useState<number | null>(() => typeof initialData?.timeSummary?.total_hours === 'number' ? initialData.timeSummary.total_hours : null);
  const [loadingTimeEntries, setLoadingTimeEntries] = useState(false);
  const [childAppointmentCount, setChildAppointmentCount] = useState(() => Number(initialData?.timeSummary?.child_appointment_count || 0));
  const [timeEntriesSource, setTimeEntriesSource] = useState<'own' | 'appointments' | null>(() => {
    const includesRelated = Boolean(initialData?.timeSummary?.includes_related);
    const childCount = Number(initialData?.timeSummary?.child_appointment_count || 0);
    return includesRelated && childCount > 0 ? 'appointments' : (hasPreloadedTimeSummary ? 'own' : null);
  });
  const [travelMinutes, setTravelMinutes] = useState<number>(() => Number(initialData?.timeSummary?.travel_minutes || 0));
  const [totalDistanceKm, setTotalDistanceKm] = useState<number>(() => Number(initialData?.timeSummary?.total_distance_km || 0));
  const [progressChildren, setProgressChildren] = useState<WorkItemProgressContext[]>(() => Array.isArray(initialData?.progressChildren) ? initialData.progressChildren as WorkItemProgressContext[] : []);

  const toDateTimeLocalInputValue = useCallback((value?: string | null) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const dateStr = getDateStrSAST(d);
    const timeParts = getTimePartsSAST(d);
    const h = String(timeParts.hours).padStart(2, '0');
    const mi = String(timeParts.minutes).padStart(2, '0');
    return `${dateStr}T${h}:${mi}`;
  }, []);

  const scheduledStartInputValue = useMemo(
    () => toDateTimeLocalInputValue(formData.scheduled_start),
    [formData.scheduled_start, toDateTimeLocalInputValue]
  );

  const scheduledEndInputValue = useMemo(
    () => toDateTimeLocalInputValue(formData.scheduled_end),
    [formData.scheduled_end, toDateTimeLocalInputValue]
  );

  const appointmentScheduleError = useMemo(
    () => getAppointmentScheduleErrorMessage(scheduledStartInputValue, scheduledEndInputValue),
    [scheduledEndInputValue, scheduledStartInputValue]
  );
  const [loadingProgressContext, setLoadingProgressContext] = useState(false);
  const [savingAppointmentCompletionOverride, setSavingAppointmentCompletionOverride] = useState(false);
  const [followUpAppointmentSubmitting, setFollowUpAppointmentSubmitting] = useState(false);
  /** Shown immediately after follow-up create + resolve, until props refresh */
  const [followUpSupersededNotice, setFollowUpSupersededNotice] = useState<{
    previousLabel: string;
    replacementNumber: string;
  } | null>(null);

  // Helper to get auth token
  const getAuthToken = useCallback(() => {
    return authService.getTokens()?.access_token || '';
  }, []);

  const shouldLoadInitialTimeEntries = useMemo(() => {
    const itemTypesWithTimeTracking = ['job', 'task', 'project', 'appointment', 'service_call', 'maintenance', 'inspection'];
    return Boolean(workItem?.item_type && itemTypesWithTimeTracking.includes(workItem.item_type));
  }, [workItem?.item_type]);

  const shouldLoadInitialProgress = useMemo(() => {
    return Boolean(workItem?.id)
      && !String(workItem.id).startsWith('temp-')
      && isOperationalHierarchyItemType(workItem.item_type);
  }, [workItem?.id, workItem?.item_type]);

  useEffect(() => {
    activeContactIdRef.current = resolvedContactId;
  }, [resolvedContactId]);

  const {
    jobTypes: configuredItemTypeOptions,
    effortConfig,
    loading: loadingItemTypeConfig,
  } = useJobConfig({
    itemType: workItem.item_type,
  });

  const [initialOverviewLoadState, setInitialOverviewLoadState] = useState<InitialOverviewLoadState>(() => createInitialOverviewLoadState({
    hasContact: resolvedContactId != null,
    shouldLoadProgress: shouldLoadInitialProgress,
    shouldLoadTimeEntries: shouldLoadInitialTimeEntries,
  }));
  const initialOverviewLoadedRef = useRef(false);

  const markInitialOverviewLoadComplete = useCallback((key: InitialOverviewLoadKey) => {
    setInitialOverviewLoadState((previous) => {
      if (!previous[key]) {
        return previous;
      }

      return {
        ...previous,
        [key]: false,
      };
    });
  }, []);

  useEffect(() => {
    if (initialDataLoading) {
      initialOverviewLoadedRef.current = false;
      return;
    }

    initialOverviewLoadedRef.current = false;
    setInitialOverviewLoadState({
      contacts: !hasPreloadedContacts,
      timeEntries: shouldLoadInitialTimeEntries && !hasPreloadedTimeSummary,
      progress: shouldLoadInitialProgress && !hasPreloadedProgressChildren,
      locations: resolvedContactId != null && !hasPreloadedLocations,
      people: resolvedContactId != null && !hasPreloadedContactPeople,
      contactDetails: resolvedContactId != null && !hasPreloadedContactDetails,
    });
  }, [hasPreloadedContactDetails, hasPreloadedContactPeople, hasPreloadedContacts, hasPreloadedLocations, hasPreloadedProgressChildren, hasPreloadedTimeSummary, initialDataLoading, resolvedContactId, shouldLoadInitialProgress, shouldLoadInitialTimeEntries, workItem?.id]);

  useEffect(() => {
    if (initialOverviewLoadedRef.current) {
      return;
    }

    const hasPendingInitialLoads = Object.values(initialOverviewLoadState).some(Boolean) || loadingItemTypeConfig;
    if (!hasPendingInitialLoads) {
      initialOverviewLoadedRef.current = true;
    }
  }, [initialOverviewLoadState, loadingItemTypeConfig]);

  const showInitialOverviewLoader = initialDataLoading;

  const initialDataAppliedRef = useRef(false);

  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    // Only apply initialData once — subsequent re-renders (from formData
    // changes, onDataChange ref changes, etc.) must not overwrite state
    // that has been loaded or modified since.
    if (initialDataAppliedRef.current) {
      return;
    }
    initialDataAppliedRef.current = true;

    if (Array.isArray(initialData?.contacts)) {
      setContacts(initialData.contacts as Contact[]);
    }

    if (Array.isArray(initialData?.locations)) {
      setLocations(initialData.locations as Location[]);
    }

    if (Array.isArray(initialData?.contactPeople)) {
      setContactPeople(initialData.contactPeople as ContactPerson[]);
    }

    if (initialData?.contactDetails !== undefined) {
      setContactDetails(initialData.contactDetails as ContactDetailsState | null ?? null);
    }

    setSelectedContactWorkItemId(
      (initialData?.contacts as Contact[] | undefined)?.find((contact) => Number(contact.id) === resolvedContactId)?.work_item_id
      || (initialData?.contactDetails as ContactDetailsState | null)?.company?.work_item_id
      || resolveStakeholderWorkItemId('contact', resolvedContactId),
    );
    setSelectedLocationWorkItemId(
      (initialData?.locations as Location[] | undefined)?.find((location) => Number(location.id) === resolvedLocationId)?.work_item_id
      || (initialData?.contactDetails as ContactDetailsState | null)?.location?.work_item_id
      || resolveStakeholderWorkItemId('location', resolvedLocationId),
    );
    setSelectedContactPersonWorkItemId(
      (initialData?.contactPeople as ContactPerson[] | undefined)?.find((person) => Number(person.id) === resolvedContactPersonId)?.work_item_id
      || resolveStakeholderWorkItemId('person', resolvedContactPersonId),
    );

    if (resolvedContactId) {
      lastLoadedContactRelationshipsRef.current = hasPreloadedLocations && hasPreloadedContactPeople ? resolvedContactId : null;
      lastLoadedContactDetailsKeyRef.current = hasPreloadedContactDetails
        ? `${resolvedContactId}:${resolvedLocationId ?? 'none'}`
        : '';
    } else {
      lastLoadedContactRelationshipsRef.current = null;
      lastLoadedContactDetailsKeyRef.current = '';
    }

    if (initialData?.timeSummary) {
      const totalHours = Number(initialData.timeSummary.total_hours || 0);
      setTimeEntriesTotal(totalHours);
      setChildAppointmentCount(Number(initialData.timeSummary.child_appointment_count || 0));
      setTimeEntriesSource(Boolean(initialData.timeSummary.includes_related) && Number(initialData.timeSummary.child_appointment_count || 0) > 0 ? 'appointments' : 'own');
      setTravelMinutes(Number(initialData.timeSummary.travel_minutes || 0));
      setTotalDistanceKm(Number(initialData.timeSummary.total_distance_km || 0));

      if (!formData.actual_hours || formData.actual_hours === 0) {
        onDataChange('actual_hours', totalHours);
      }
    } else if (!shouldLoadInitialTimeEntries) {
      setTimeEntriesTotal(null);
      setChildAppointmentCount(0);
      setTimeEntriesSource(null);
      setTravelMinutes(0);
      setTotalDistanceKm(0);
    }

    if (Array.isArray(initialData?.progressChildren)) {
      setProgressChildren(initialData.progressChildren as WorkItemProgressContext[]);
    } else if (!shouldLoadInitialProgress) {
      setProgressChildren([]);
    }

  }, [formData.actual_hours, hasPreloadedContactDetails, hasPreloadedContactPeople, hasPreloadedLocations, initialData, initialDataLoading, onDataChange, resolvedContactId, resolvedContactPersonId, resolvedLocationId, shouldLoadInitialProgress, shouldLoadInitialTimeEntries]);

  const appointmentSubtypeOptions = useMemo<ConfigurableTypeOption[]>(() => {
    const currentSubtype = (formData.item_subtype || workItem.item_subtype || '').trim();
    const currentSubtypeKey = normalizeAppointmentTypeKey(currentSubtype);
    const baseTypes = configuredItemTypeOptions.length > 0 ? configuredItemTypeOptions : DEFAULT_APPOINTMENT_TYPES;

    if (
      !currentSubtypeKey ||
      baseTypes.some(type => normalizeAppointmentTypeKey(type.key) === currentSubtypeKey)
    ) {
      return baseTypes;
    }

    return [...baseTypes, { key: currentSubtypeKey, label: formatSubtypeLabel(currentSubtypeKey) }];
  }, [configuredItemTypeOptions, formData.item_subtype, workItem.item_subtype]);

  const availableSubtypeOptions = useMemo<ConfigurableTypeOption[]>(() => {
    const currentSubtype = (formData.item_subtype || workItem.item_subtype || '').trim();

    if (workItem.item_type === 'job') {
      if (!currentSubtype || jobTypes.some(type => type.key === currentSubtype)) {
        return jobTypes;
      }

      return [...jobTypes, { key: currentSubtype, label: formatSubtypeLabel(currentSubtype) }];
    }

    if (workItem.item_type === 'appointment') {
      return appointmentSubtypeOptions;
    }

    return [];
  }, [appointmentSubtypeOptions, formData.item_subtype, jobTypes, workItem.item_subtype, workItem.item_type]);

  // Memoize individual custom field sources to prevent cascading re-renders
  const workItemCustomFields = useMemo(() => {
    return typeof workItem.custom_fields === 'object' && workItem.custom_fields
      ? workItem.custom_fields
      : {};
  }, [workItem.custom_fields]);

  const formCustomFields = useMemo(() => {
    return typeof formData.custom_fields === 'object' && formData.custom_fields
      ? formData.custom_fields
      : {};
  }, [formData.custom_fields]);

  const mergedCustomFields = useMemo<Record<string, any>>(() => {
    return {
      ...workItemCustomFields,
      ...formCustomFields,
    };
  }, [workItemCustomFields, formCustomFields]);

  const appointmentRuleWorkItem = useMemo(() => (
    workItem.item_type === 'appointment'
      ? {
        ...workItem,
        item_type: 'appointment',
        item_subtype: formData.item_subtype || workItem.item_subtype || null,
        custom_fields: mergedCustomFields,
      }
      : null
  ), [formData.item_subtype, mergedCustomFields, workItem]);

  const appointmentTypeRules = useMemo(
    () => appointmentRuleWorkItem
      ? getAppointmentTypeRules(appointmentRuleWorkItem, appointmentSubtypeOptions)
      : null,
    [appointmentRuleWorkItem, appointmentSubtypeOptions]
  );

  const isPurchaseAppointment = useMemo(
    () => workItem.item_type === 'appointment'
      && normalizeAppointmentTypeKey(formData.item_subtype || workItem.item_subtype || '') === PURCHASE_APPOINTMENT_TYPE_KEY,
    [formData.item_subtype, workItem.item_subtype, workItem.item_type]
  );

  const parentJobIdForFollowUp = useMemo(
    () => (workItem.item_type === 'appointment' ? resolveParentJobIdFromAppointment(workItem) : null),
    [workItem],
  );

  const showFollowUpNewAppointmentButton = useMemo(
    () => workItem.item_type === 'appointment'
      && Boolean(parentJobIdForFollowUp)
      && shouldOfferNewFollowUpAppointment(workItem)
      && !mergedCustomFields.replaced_by_follow_up_appointment_id
      && !followUpSupersededNotice,
    [
      followUpSupersededNotice,
      mergedCustomFields.replaced_by_follow_up_appointment_id,
      parentJobIdForFollowUp,
      workItem,
    ],
  );

  const followUpResolutionContextPhrase = useMemo(() => {
    const t = getWorkItemResolutionType(workItem);
    if (t === 'partial') {
      return 'partial';
    }
    if (t === 'rescheduled') {
      return 'rescheduled';
    }
    if (t === 'cancelled') {
      return 'cancelled';
    }
    return 'closed out';
  }, [workItem]);

  const supersededByFollowUpDisplay = useMemo(() => {
    const fromServer = mergedCustomFields.replaced_by_follow_up_appointment_number;
    if (typeof fromServer === 'string' && fromServer.trim()) {
      return fromServer.trim();
    }
    return followUpSupersededNotice?.replacementNumber || null;
  }, [followUpSupersededNotice?.replacementNumber, mergedCustomFields.replaced_by_follow_up_appointment_number]);

  const purchaseItems = useMemo(
    () => normalizePurchaseListItems(mergedCustomFields.purchase_items),
    [mergedCustomFields]
  );

  const handlePurchaseItemsChange = useCallback((items: PurchaseListItem[]) => {
    onDataChange('custom_fields', {
      ...mergedCustomFields,
      purchase_items: items,
    });
  }, [mergedCustomFields, onDataChange]);

  useEffect(() => {
    setFollowUpSupersededNotice(null);
  }, [workItem.id]);

  const handleCreateFollowUpAppointment = useCallback(async () => {
    if (!parentJobIdForFollowUp || workItem.item_type !== 'appointment' || followUpAppointmentSubmitting) {
      return;
    }

    const currentLabel = workItem.item_number?.trim() || `this appointment (${String(workItem.id).slice(0, 8)}…)`;

    const accepted = await confirm({
      title: 'Create follow-up and resolve this visit?',
      message: `A new appointment will be created on the same parent job. The current appointment (${currentLabel}), currently ${followUpResolutionContextPhrase}, will be marked resolved and linked to the new appointment number (for example APT352). You can edit the new visit afterward.`,
      confirmText: 'Create & resolve',
      cancelText: 'Cancel',
      variant: 'info',
    });

    if (!accepted) {
      return;
    }

    const tokens = authService.getTokens();
    if (!tokens?.access_token) {
      notify.error('Sign in to create an appointment');
      return;
    }

    setFollowUpAppointmentSubmitting(true);

    try {
      const parentRes = await fetch(`/api/v1/work-items/${parentJobIdForFollowUp}`, {
        headers: { Authorization: `Bearer ${tokens.access_token}` },
      });
      const parentJson = await parentRes.json().catch(() => null);
      if (!parentRes.ok || !parentJson?.data) {
        notify.error(typeof parentJson?.error === 'string' ? parentJson.error : 'Could not load the parent job');
        return;
      }

      const parentJob = parentJson.data as WorkItem;
      const schedule = getDefaultAppointmentSchedule(new Date(), 60);
      const scheduledStart = new Date(schedule.scheduled_start);
      const scheduledEnd = new Date(schedule.scheduled_end);

      const assigneeIds = getAssigneeUserIdsFromWorkItem(workItem);
      const currentUser = authService.getUser();
      const currentUserId = typeof currentUser?.id === 'number'
        ? currentUser.id
        : Number(currentUser?.id);
      const primaryAssigneeId = assigneeIds[0]
        ?? (Number.isFinite(currentUserId) && currentUserId > 0 ? currentUserId : null);

      if (!primaryAssigneeId) {
        notify.error('Could not determine an assignee. Assign someone to this appointment, then try again.');
        return;
      }

      // Credit-limit pre-flight for the follow-up appointment.
      const followUpContactId = workItem.contact_id ?? parentJob.contact_id ?? null;
      if (followUpContactId) {
        const { warnIfContactAccountIssue } = await import('@/lib/contacts/accountWarning');
        const proceed = await warnIfContactAccountIssue({
          contactId: followUpContactId,
          action: 'create this follow-up appointment',
          documentType: 'appointment',
        });
        if (!proceed) return;
      }

      const coords = extractCoordinates(workItem.location_coordinates ?? (workItem as any).location_coordinates);
      const titleBase = workItem.title?.trim() || parentJob.title || 'Follow-up appointment';
      const cf = (workItem.custom_fields || {}) as Record<string, any>;

      const createRes = await fetch('/api/v1/work-items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          action: 'create',
          item_type: 'appointment',
          parent_id: parentJobIdForFollowUp,
          title: `${titleBase} (follow-up)`,
          description: workItem.description || parentJob.description || undefined,
          notes: (workItem as any).notes || cf?.additional_notes || undefined,
          item_subtype: formData.item_subtype || workItem.item_subtype || undefined,
          priority: workItem.priority || parentJob.priority || 'medium',
          custom_fields: {
            estimated_travel_minutes: typeof cf.estimated_travel_minutes === 'number' ? cf.estimated_travel_minutes : 30,
            parent_job_id: parentJobIdForFollowUp,
            parent_job_number: parentJob.item_number,
            parent_job_title: parentJob.title,
            require_location_verification: Boolean(
              cf.require_exact_location
              || cf.require_location_verification
              || (parentJob.custom_fields as any)?.require_exact_location
              || (parentJob.custom_fields as any)?.require_location_verification,
            ),
            geofence_radius_meters: cf.geofence_radius_meters
              || (parentJob.custom_fields as any)?.geofence_radius_meters
              || 100,
            equipment_required: cf.equipment_required || (parentJob.custom_fields as any)?.equipment_required,
            safety_requirements: cf.safety_requirements || (parentJob.custom_fields as any)?.safety_requirements,
            follow_up_from_appointment_id: String(workItem.id),
            ...(workItem.item_number ? { follow_up_from_appointment_number: workItem.item_number } : {}),
          },
          scheduled_start: formatLocalISOSAST(scheduledStart),
          scheduled_end: formatLocalISOSAST(scheduledEnd),
          estimated_hours: formData.estimated_hours
            ? Number(formData.estimated_hours)
            : (workItem.estimated_hours || parentJob.estimated_hours || 2),
          assigned_to: primaryAssigneeId,
          assigned_users: assigneeIds.length > 0 ? assigneeIds : [primaryAssigneeId],
          status: 'scheduled',
          module_context: workItem.module_context || parentJob.module_context || undefined,
          contact_id: workItem.contact_id ?? parentJob.contact_id ?? undefined,
          contact_name: workItem.contact_name ?? parentJob.contact_name ?? undefined,
          contact_phone: workItem.contact_phone ?? (parentJob as any).contact_phone ?? undefined,
          contact_email: workItem.contact_email ?? (parentJob as any).contact_email ?? undefined,
          contact_person: (workItem as any).contact_person ?? undefined,
          contact_person_id: (workItem as any).contact_person_id ?? undefined,
          contact_person_id2: workItem.contact_person_id2 ?? undefined,
          contact_person_id3: workItem.contact_person_id3 ?? undefined,
          location_id: workItem.location_id ?? undefined,
          location_name: workItem.location_name ?? undefined,
          location_address: workItem.location_address ?? undefined,
          location_coordinates: coords || undefined,
        }),
      });

      const apptData = await createRes.json();
      if (!apptData.success) {
        if (apptData.code === 'CREDIT_LIMIT_EXCEEDED') {
          const contactName = apptData.details?.contact_name;
          notify.error(contactName ? `${contactName}: ${apptData.error}` : apptData.error || 'Credit limit exceeded');
          return;
        }
        notify.error(apptData.error || 'Failed to create appointment');
        return;
      }

      const createdAppointmentId = apptData.data?.id as string | undefined;
      if (!createdAppointmentId) {
        notify.error('Failed to create appointment');
        return;
      }

      const newAppointmentNumber = typeof apptData.data?.item_number === 'string' && apptData.data.item_number.trim()
        ? apptData.data.item_number.trim()
        : null;
      const replacementLabel = newAppointmentNumber || `new appointment (${String(createdAppointmentId).slice(0, 8)}…)`;

      const supersededAt = new Date().toISOString();
      const previousLabel = workItem.item_number?.trim()
        || `Appointment ${String(workItem.id).slice(0, 8)}…`;

      const patchRes = await fetch(`/api/v1/work-items/${workItem.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          status: 'resolved',
          resolution_type: 'resolved',
          resolution_notes: `${previousLabel} was superseded by follow-up appointment ${replacementLabel} created from the appointment overview.`,
          custom_fields: {
            replaced_by_follow_up_appointment_id: createdAppointmentId,
            ...(newAppointmentNumber ? { replaced_by_follow_up_appointment_number: newAppointmentNumber } : {}),
            replaced_by_follow_up_at: supersededAt,
          },
        }),
      });

      const patchJson = await patchRes.json().catch(() => null);
      if (!patchRes.ok || !patchJson?.success) {
        notify.error(
          typeof patchJson?.error === 'string'
            ? patchJson.error
            : 'Follow-up was created but this appointment could not be marked resolved automatically. Resolve it manually.',
        );
      } else {
        onDataChange('status', 'resolved');
        onDataChange('resolution_type', 'resolved');
        onDataChange('custom_fields', {
          ...mergedCustomFields,
          replaced_by_follow_up_appointment_id: createdAppointmentId,
          ...(newAppointmentNumber ? { replaced_by_follow_up_appointment_number: newAppointmentNumber } : {}),
          replaced_by_follow_up_at: supersededAt,
          resolution_type: 'resolved',
        });
        setFollowUpSupersededNotice({
          previousLabel,
          replacementNumber: replacementLabel,
        });
      }

      try {
        await fetch('/api/v1/work-item-relationships', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${tokens.access_token}`,
          },
          body: JSON.stringify({
            action: 'create',
            sourceItemId: createdAppointmentId,
            targetItemId: parentJobIdForFollowUp,
            relationshipType: 'child_of',
          }),
        });
      } catch (relErr) {
        console.warn('[UnifiedOverviewTab] Relationship creation failed for follow-up appointment:', relErr);
      }

      const assigneesForThreads = assigneeIds.length > 0 ? assigneeIds : [primaryAssigneeId];
      await Promise.all(assigneesForThreads.map((assigneeId) => (
        fetch('/api/v1/schedule-threads', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tokens.access_token}` },
          body: JSON.stringify({ user_id: assigneeId }),
        }).catch(() => null)
      )));

      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(WORK_ITEM_LIST_CHANGED_EVENT, {
          detail: {
            workItemIds: [createdAppointmentId, parentJobIdForFollowUp, String(workItem.id)],
            source: 'unified-overview-follow-up-appointment',
          },
        }));
      }

      if (patchRes.ok && patchJson?.success) {
        notify.success(
          `Resolved ${previousLabel}. This visit was superseded by new appointment ${replacementLabel} on the same parent job.`,
        );
      }
    } catch (error: unknown) {
      console.error('[UnifiedOverviewTab] Follow-up appointment:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to create appointment');
    } finally {
      setFollowUpAppointmentSubmitting(false);
    }
  }, [
    followUpAppointmentSubmitting,
    followUpResolutionContextPhrase,
    formData.estimated_hours,
    formData.item_subtype,
    mergedCustomFields,
    onDataChange,
    parentJobIdForFollowUp,
    workItem,
  ]);

  const resolveParentJobOnResolve = useMemo(() => {
    if (workItem.item_type !== 'appointment') {
      return false;
    }

    return Boolean(
      mergedCustomFields.resolve_parent_job_on_resolve
      ?? mergedCustomFields.is_final_appointment
      ?? formData.is_final_appointment
      ?? (workItem as any).is_final_appointment
    );
  }, [formData.is_final_appointment, mergedCustomFields, workItem]);

  const handleResolveParentJobOnResolveChange = useCallback((enabled: boolean) => {
    const nextCustomFields = { ...mergedCustomFields };

    if (enabled) {
      nextCustomFields.resolve_parent_job_on_resolve = true;
      nextCustomFields.is_final_appointment = true;
    } else {
      delete nextCustomFields.resolve_parent_job_on_resolve;
      delete nextCustomFields.is_final_appointment;
    }

    onDataChange('custom_fields', nextCustomFields);
    onDataChange('is_final_appointment', enabled);
  }, [mergedCustomFields, onDataChange]);

  const currentAppointmentAssigneeName = useMemo(() => {
    const rawAssignee = (formData.assigned_to_name
      ?? (workItem as any).assigned_to_name
      ?? (workItem as any).assignee_name
      ?? mergedCustomFields.assigned_to_name) as unknown;

    return typeof rawAssignee === 'string' && rawAssignee.trim() ? rawAssignee.trim() : null;
  }, [formData.assigned_to_name, mergedCustomFields.assigned_to_name, workItem]);

  const currentAppointmentAssigneeId = useMemo(() => {
    const rawAssigneeId = (formData.assigned_to
      ?? (workItem as any).assigned_to
      ?? mergedCustomFields.assigned_to) as unknown;

    if (typeof rawAssigneeId === 'number' && Number.isFinite(rawAssigneeId)) {
      return rawAssigneeId;
    }

    if (typeof rawAssigneeId === 'string' && rawAssigneeId.trim()) {
      const numeric = Number(rawAssigneeId);
      return Number.isFinite(numeric) ? numeric : null;
    }

    return null;
  }, [formData.assigned_to, mergedCustomFields.assigned_to, workItem]);

  const appointmentAssigneeRoster = useMemo(() => {
    if (workItem.item_type !== 'appointment') {
      return [] as Array<{ user_id: number; display_name: string }>;
    }

    const seenUserIds = new Set<number>();
    const roster: Array<{ user_id: number; display_name: string }> = [];

    const pushUser = (userId: unknown, displayName?: unknown) => {
      const numericUserId = typeof userId === 'number'
        ? userId
        : typeof userId === 'string' && userId.trim()
          ? Number(userId)
          : Number.NaN;

      if (!Number.isFinite(numericUserId) || seenUserIds.has(numericUserId)) {
        return;
      }

      seenUserIds.add(numericUserId);
      roster.push({
        user_id: numericUserId,
        display_name: typeof displayName === 'string' && displayName.trim()
          ? displayName.trim()
          : `User ${numericUserId}`,
      });
    };

    (Array.isArray(workItem.assignees) ? workItem.assignees : []).forEach((assignee) => {
      pushUser(
        assignee.user_id,
        `${assignee.first_name || ''} ${assignee.last_name || ''}`.trim() || assignee.email || undefined,
      );
    });

    pushUser(workItem.assigned_to, workItem.assigned_to_name || workItem.assignee_name || undefined);

    const customFieldUserIds = [
      mergedCustomFields.assigned_user_ids,
      mergedCustomFields.participant_user_ids,
      mergedCustomFields.assigned_users,
    ];

    customFieldUserIds.forEach((value) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => {
          if (typeof entry === 'object' && entry) {
            pushUser((entry as any).user_id ?? (entry as any).id, (entry as any).name ?? (entry as any).display_name);
          } else {
            pushUser(entry);
          }
        });
      }
    });

    return roster;
  }, [mergedCustomFields.assigned_user_ids, mergedCustomFields.assigned_users, mergedCustomFields.participant_user_ids, workItem.assigned_to, workItem.assigned_to_name, workItem.assignee_name, workItem.assignees, workItem.item_type]);

  const currentUser = authService.getUser();
  const canManageAppointmentCompletionOverride = userCanManageAppointmentCompletionOverride(currentUser);
  const persistedAppointmentCompletionOverride = useMemo(
    () => getAppointmentCompletionOverrideFormState(mergedCustomFields),
    [mergedCustomFields],
  );
  const persistedAppointmentCompletionOverrideKey = useMemo(
    () => getAppointmentCompletionOverrideFormStateKey(persistedAppointmentCompletionOverride),
    [persistedAppointmentCompletionOverride],
  );
  const [appointmentCompletionOverrideForm, setAppointmentCompletionOverrideForm] = useState<AppointmentCompletionOverrideFormState>(
    persistedAppointmentCompletionOverride,
  );

  useEffect(() => {
    setAppointmentCompletionOverrideForm(persistedAppointmentCompletionOverride);
  }, [persistedAppointmentCompletionOverride, persistedAppointmentCompletionOverrideKey]);

  const completionOverrideReasonTrimmed = appointmentCompletionOverrideForm.reason.trim();
  const isAppointmentCompletionOverrideDirty = appointmentCompletionOverrideForm.enabled !== persistedAppointmentCompletionOverride.enabled
    || completionOverrideReasonTrimmed !== persistedAppointmentCompletionOverride.reason.trim();
  const isAppointmentCompletionOverrideReasonValid = !appointmentCompletionOverrideForm.enabled
    || completionOverrideReasonTrimmed.length >= APPOINTMENT_COMPLETION_OVERRIDE_REASON_MIN_LENGTH;
  const appointmentCompletionOverrideAction: 'create' | 'update' | 'remove' | 'none' = persistedAppointmentCompletionOverride.enabled
    ? appointmentCompletionOverrideForm.enabled
      ? (isAppointmentCompletionOverrideDirty ? 'update' : 'remove')
      : 'remove'
    : appointmentCompletionOverrideForm.enabled
      ? 'create'
      : 'none';
  const canSubmitAppointmentCompletionOverrideAction = appointmentCompletionOverrideAction === 'none'
    || appointmentCompletionOverrideAction === 'remove'
    || ((appointmentCompletionOverrideAction === 'create' || appointmentCompletionOverrideAction === 'update')
      && isAppointmentCompletionOverrideReasonValid);
  const showAppointmentCompletionOverrideCard = workItem.item_type === 'appointment'
    && (canManageAppointmentCompletionOverride || persistedAppointmentCompletionOverride.enabled);
  const appointmentOverrideAssigneeUserIds = useMemo(
    () => Array.from(new Set(
      appointmentAssigneeRoster
        .map((assignee) => assignee.user_id)
        .filter((userId) => Number.isFinite(userId) && userId > 0),
    )),
    [appointmentAssigneeRoster],
  );
  const appointmentParentJobId = useMemo(() => {
    if (typeof workItem.parent_id === 'string' && workItem.parent_id.trim()) {
      return workItem.parent_id;
    }

    const rawParentJobId = mergedCustomFields.parent_job_id;
    if (typeof rawParentJobId === 'string' && rawParentJobId.trim()) {
      return rawParentJobId.trim();
    }

    if (typeof rawParentJobId === 'number' && Number.isFinite(rawParentJobId)) {
      return String(rawParentJobId);
    }

    return null;
  }, [mergedCustomFields.parent_job_id, workItem.parent_id]);

  // ────────────────────────────────────────────────────────────────────
  // SUBTASKS STATE & LOGIC (inline on overview tab)
  // ────────────────────────────────────────────────────────────────────
  const effectiveWorkItemId = workItemId || workItem.id;
  const effectiveWorkItemType = workItemType || workItem.item_type;
  // Show subtasks for task-like items (not inventory, not finance docs)
  const showSubtasksSection = !['inventory', 'product', 'service', 'invoice', 'quote', 'expense', 'credit_note', 'delivery_note', 'grn', 'proforma', 'purchase_order', 'sales_order', 'project', 'team_member', 'recurring_job'].includes(effectiveWorkItemType);

  const [subtasks, setSubtasks] = useState<Subtask[]>(() => Array.isArray(initialData?.subtasks) ? initialData.subtasks as Subtask[] : []);
  const [loadingSubtasks, setLoadingSubtasks] = useState(false);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddLoading, setQuickAddLoading] = useState(false);
  const [subtasksExpanded, setSubtasksExpanded] = useState(true);
  const quickAddInputRef = useRef<HTMLInputElement>(null);
  const subtasksLoadedRef = useRef(Boolean(initialData?.subtasks));

  // Refs for callbacks to prevent render loops
  const loadSubtasksRef = useRef<(() => Promise<void>) | null>(null);
  const loadContactsRef = useRef<(() => Promise<void>) | null>(null);
  const loadTimeEntriesRef = useRef<(() => Promise<void>) | null>(null);
  const loadLocationsRef = useRef<(contactId: number) => Promise<void> | null>(null);
  const loadContactPeopleRef = useRef<(contactId: number) => Promise<void> | null>(null);
  const loadSelectedContactDetailsRef = useRef<(() => Promise<void>) | null>(null);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const loadSubtasks = useCallback(async () => {
    if (!effectiveWorkItemId) return;
    // Skip for synthetic IDs (team members) that aren't real work items
    if (effectiveWorkItemId.startsWith('team-member-')) return;

    try {
      setLoadingSubtasks(true);
      const response = await fetch(`/api/v1/work-items/${effectiveWorkItemId}/subtasks`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
      });

      const data = await response.json().catch(() => null);
      if (response.ok && data?.success) {
        setSubtasks(Array.isArray(data.data) ? data.data : []);
      } else {
        setSubtasks([]);
      }
    } catch (error) {
      console.error('Error loading subtasks:', error);
      setSubtasks([]);
    } finally {
      setLoadingSubtasks(false);
    }
  }, [effectiveWorkItemId, getAuthToken]);

  // Store latest callbacks in refs
  useEffect(() => {
    loadSubtasksRef.current = loadSubtasks;
  }, [loadSubtasks]);

  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    if (Array.isArray(initialData?.subtasks)) {
      setSubtasks(initialData.subtasks as Subtask[]);
      subtasksLoadedRef.current = true;
    } else {
      subtasksLoadedRef.current = false;
    }
  }, [initialData?.subtasks, initialDataLoading]);

  // Load subtasks on mount
  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    if (showSubtasksSection && !subtasksLoadedRef.current) {
      subtasksLoadedRef.current = true;
      void loadSubtasks();
    }
  }, [initialDataLoading, showSubtasksSection, loadSubtasks]);

  // Notify parent of count changes
  useEffect(() => {
    onSubtasksCountChange?.(subtasks.length);
  }, [subtasks.length, onSubtasksCountChange]);

  // Focus quick add input when shown or when title is cleared
  useEffect(() => {
    if (showQuickAdd && quickAddInputRef.current) {
      quickAddInputRef.current.focus();
    }
  }, [showQuickAdd, quickAddTitle]);

  const handleQuickAddSubtask = useCallback(async () => {
    if (!quickAddTitle.trim() || !effectiveWorkItemId) return;

    try {
      setQuickAddLoading(true);

      // Get assignees from parent work item to inherit on subtask
      let parentAssigneeIds = getAssigneeUserIdsFromWorkItem(workItem);

      // If no assignees found in workItem object, fetch from API
      if (parentAssigneeIds.length === 0) {
        try {
          const assigneesRes = await fetch(`/api/v1/work-items/${effectiveWorkItemId}/assignees`, {
            headers: { 'Authorization': `Bearer ${getAuthToken()}` }
          });
          if (assigneesRes.ok) {
            const assigneesData = await assigneesRes.json();
            if (assigneesData.success && assigneesData.data) {
              parentAssigneeIds = assigneesData.data
                .map((a: any) => Number(a.user_id))
                .filter((id: number) => Number.isFinite(id) && id > 0);
            }
          }
        } catch (err) {
          console.warn('Failed to fetch assignees from API:', err);
        }
      }

      const primaryAssignee = parentAssigneeIds.length > 0 ? parentAssigneeIds[0] : null;

      const response = await fetch(`/api/v1/work-items/${effectiveWorkItemId}/subtasks`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          title: quickAddTitle.trim(),
          item_type: effectiveWorkItemType === 'epic' ? 'story' : 'subtask',
          status: 'open',
          priority: 'medium',
          assigned_to: primaryAssignee,
          assignees: parentAssigneeIds
        })
      });

      const data = await response.json().catch(() => null);

      if (response.ok && data?.success) {
        setSubtasks(prev => [...prev, data.data]);
        setQuickAddTitle('');
        // Keep quick-add open for rapid entry
      } else {
        const errorMessage = data?.error || 'Failed to create subtask';
        notify.error(errorMessage);
      }
    } catch (error) {
      console.error('Error creating subtask:', error);
      notify.error('Failed to create subtask. Please try again.');
    } finally {
      setQuickAddLoading(false);
    }
  }, [effectiveWorkItemId, effectiveWorkItemType, getAuthToken, quickAddTitle, workItem]);

  const handleToggleSubtaskStatus = useCallback(async (subtask: Subtask) => {
    const newStatus = isSubtaskCompleted(subtask.status) ? 'open' : 'resolved';
    // Optimistic update
    setSubtasks(prev => prev.map(s =>
      s.id === subtask.id
        ? { ...s, status: newStatus, progress_percentage: newStatus === 'open' ? 0 : 100 }
        : s
    ));
    try {
      await fetch(`/api/v1/work-items/${subtask.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
    } catch (error) {
      console.error('Error updating subtask status:', error);
      // Revert on failure
      setSubtasks(prev => prev.map(s =>
        s.id === subtask.id ? { ...s, status: subtask.status, progress_percentage: subtask.progress_percentage } : s
      ));
    }
  }, [getAuthToken]);

  const handleRemoveSubtask = useCallback(async (subtask: Subtask) => {
    const confirmed = await confirm({
      title: 'Remove Subtask',
      message: 'Remove this subtask? The work item will not be deleted, only the relationship.',
      confirmText: 'Remove',
      variant: 'danger'
    });
    if (!confirmed) return;
    try {
      const response = await fetch(`/api/v1/work-items/${effectiveWorkItemId}/subtasks/${subtask.relationship_id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (response.ok) {
        setSubtasks(prev => prev.filter(s => s.id !== subtask.id));
        notify.success('Subtask removed');
      } else {
        notify.error('Failed to remove subtask');
      }
    } catch (error) {
      console.error('Error removing subtask:', error);
      notify.error('Failed to remove subtask');
    }
  }, [effectiveWorkItemId, getAuthToken]);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setSubtasks(prev => {
      const oldIndex = prev.findIndex(s => s.id === active.id);
      const newIndex = prev.findIndex(s => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return prev;
      return arrayMove(prev, oldIndex, newIndex);
    });
  }, []);

  const subtaskIds = useMemo(() => subtasks.map(s => s.id), [subtasks]);
  const completedSubtasks = subtasks.filter(s => isSubtaskCompleted(s.status)).length;
  const subtaskProgress = subtasks.length > 0 ? Math.round((completedSubtasks / subtasks.length) * 100) : 0;

  // ────────────────────────────────────────────────────────────────────
  // END SUBTASKS LOGIC
  // ────────────────────────────────────────────────────────────────────

  // Track whether user has manually toggled mode to prevent auto-detection overriding
  const userToggledModeRef = useRef(false);

  // Detect initial location source for appointments
  useEffect(() => {
    // Reset manual toggle flag when item changes
    userToggledModeRef.current = false;
  }, [workItem?.id]);

  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    if (workItem?.item_type === 'appointment' && !userToggledModeRef.current) {
      // Use formData (fresh) over workItem (potentially stale) for detection
      const contactId = formData.contact_id || workItem.contact_id;
      const contactName = formData.contact_name || workItem.contact_name;
      const locName = formData.location_name || workItem.location_name || '';
      const locAddr = formData.location_address || workItem.location_address || '';
      const coords = formData.location_coordinates || workItem.location_coordinates || null;
      const locationId = formData.location_id || workItem.location_id;
      const hasLocationData = !!(locName || locAddr || coords);
      const hasLocationId = !!locationId;

      if (hasLocationData && !hasLocationId) {
        // Custom location — no linked contact_location
        setLocationSource('custom');
        setCustomLocSearch(locName);
        let lat = '', lng = '';
        if (coords) {
          if (typeof coords === 'object' && 'lat' in coords && 'lng' in coords) {
            lat = String(coords.lat);
            lng = String(coords.lng);
          } else if (typeof coords === 'object' && 'x' in coords && 'y' in coords) {
            lat = String(coords.x);
            lng = String(coords.y);
          } else if (typeof coords === 'string') {
            const coordStr = coords.replace(/[()]/g, '');
            const parts = coordStr.split(',').map((s: string) => s.trim());
            if (parts.length === 2) { lat = parts[0]; lng = parts[1]; }
          }
        }
        setCustomLocation({ name: locName, address: locAddr, lat, lng });
      } else {
        setLocationSource('contact');
      }
    }
  }, [formData.contact_id, formData.contact_name, formData.location_address, formData.location_coordinates, formData.location_id, formData.location_name, initialDataLoading, workItem?.contact_id, workItem?.contact_name, workItem?.id, workItem?.item_type, workItem?.location_address, workItem?.location_coordinates, workItem?.location_id, workItem?.location_name]);

  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    if (hasPreloadedProgressChildren) {
      setLoadingProgressContext(false);
      markInitialOverviewLoadComplete('progress');
      return;
    }

    if (!shouldLoadInitialProgress) {
      setProgressChildren([]);
      setLoadingProgressContext(false);
      markInitialOverviewLoadComplete('progress');
      return;
    }

    let cancelled = false;
    setLoadingProgressContext(true);

    fetch(`/api/v1/work-items/${workItem.id}/progress`, {
      headers: { 'Authorization': `Bearer ${getAuthToken()}` }
    })
      .then(async (response) => {
        if (!response.ok) {
          return null;
        }

        return response.json();
      })
      .then((data) => {
        if (cancelled) {
          return;
        }

        setProgressChildren(Array.isArray(data?.data?.children) ? data.data.children : []);
      })
      .catch((error) => {
        console.error('Error loading progress context:', error);
        if (!cancelled) {
          setProgressChildren([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingProgressContext(false);
          markInitialOverviewLoadComplete('progress');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [getAuthToken, hasPreloadedProgressChildren, initialDataLoading, markInitialOverviewLoadComplete, shouldLoadInitialProgress, workItem?.id, workItem?.item_type]);

  const loadContacts = useCallback(async () => {
    if (hasPreloadedContacts) {
      setLoadingContacts(false);
      markInitialOverviewLoadComplete('contacts');
      return;
    }

    setLoadingContacts(true);
    try {
      if (contextLoadContacts) {
        const nextContacts = await contextLoadContacts({
          contactTypes: ['customer'],
          limit: 200,
        });
        setContacts(nextContacts as Contact[]);
        return;
      }

      const response = await fetch('/api/v1/contacts?contact_types=customer&limit=200', {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setContacts(data.data || []);
        }
      }
    } catch (error) {
      console.error('Error loading contacts:', error);
    } finally {
      setLoadingContacts(false);
      markInitialOverviewLoadComplete('contacts');
    }
  }, [contextLoadContacts, getAuthToken, hasPreloadedContacts, markInitialOverviewLoadComplete]);

  const loadLocations = useCallback(async (contactId: number) => {
    activeContactIdRef.current = contactId;
    const requestId = ++latestLocationLoadRequestId.current;
    setLoadingLocations(true);
    try {
      // Use shared context if available
      if (contextLoadContactLocations) {
        const locs = await contextLoadContactLocations(contactId);
        if (requestId !== latestLocationLoadRequestId.current || activeContactIdRef.current !== contactId) {
          return;
        }
        setLocations(locs as Location[]);
        return;
      }
      // Fallback: fetch directly
      const response = await fetch(`/api/v1/contacts/${contactId}/locations`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (requestId !== latestLocationLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setLocations(data.data || []);
        } else {
          setLocations([]);
        }
      } else {
        setLocations([]);
      }
    } catch (error) {
      if (requestId !== latestLocationLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }
      console.error('Error loading locations:', error);
      setLocations([]);
    } finally {
      if (requestId === latestLocationLoadRequestId.current && activeContactIdRef.current === contactId) {
        setLoadingLocations(false);
        markInitialOverviewLoadComplete('locations');
      }
    }
  }, [contextLoadContactLocations, getAuthToken, markInitialOverviewLoadComplete]);

  useEffect(() => {
    loadLocationsRef.current = loadLocations;
  }, [loadLocations]);

  const loadContactPeople = useCallback(async (contactId: number) => {
    activeContactIdRef.current = contactId;
    const requestId = ++latestPeopleLoadRequestId.current;
    setLoadingPeople(true);
    try {
      // Use shared context if available
      if (contextLoadContactPeople) {
        const people = await contextLoadContactPeople(contactId);
        if (requestId !== latestPeopleLoadRequestId.current || activeContactIdRef.current !== contactId) {
          return;
        }
        setContactPeople(people as ContactPerson[]);
        return;
      }
      // Fallback: fetch directly
      if (requestId !== latestPeopleLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }
      const people = await fetchContactPeople<ContactPerson>({
        contactId,
        token: getAuthToken(),
      });
      if (people.length > 0) {
        setContactPeople(people);
      } else {
        setContactPeople([]);
      }
    } catch (error) {
      if (requestId !== latestPeopleLoadRequestId.current || activeContactIdRef.current !== contactId) {
        return;
      }
      console.error('Error loading contact people:', error);
      setContactPeople([]);
    } finally {
      if (requestId === latestPeopleLoadRequestId.current && activeContactIdRef.current === contactId) {
        setLoadingPeople(false);
        markInitialOverviewLoadComplete('people');
      }
    }
  }, [contextLoadContactPeople, getAuthToken, markInitialOverviewLoadComplete]);

  // Load contacts on mount
  const loadTimeEntries = useCallback(async () => {
    if (!workItem?.id) return;

    if (hasPreloadedTimeSummary) {
      setLoadingTimeEntries(false);
      markInitialOverviewLoadComplete('timeEntries');
      return;
    }

    setLoadingTimeEntries(true);
    try {
      // For jobs: include related appointment time entries (sum of child appointment WIP timers)
      // For appointments: only own time entries (this appointment's WIP timer)
      const isJob = workItem.item_type === 'job';
      const includeRelated = isJob || workItem.item_type === 'project';
      const url = `/api/v1/work-items/${workItem.id}/time-entries/summary${includeRelated ? '?include_related=true' : ''}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const totalHours = data.data.total_hours || 0;
          setTimeEntriesTotal(totalHours);
          setChildAppointmentCount(data.data.child_appointment_count || 0);
          setTimeEntriesSource(isJob && (data.data.child_appointment_count || 0) > 0 ? 'appointments' : 'own');
          setTravelMinutes(data.data.travel_minutes || 0);
          setTotalDistanceKm(data.data.total_distance_km || 0);
          // Auto-update actual_hours if not manually set
          if (!formData.actual_hours || formData.actual_hours === 0) {
            onDataChange('actual_hours', totalHours);
          }
        } else if (data.success && data.summary) {
          // Fallback for older response format
          const totalHours = data.summary.total_duration_hours || data.summary.total_hours || 0;
          setTimeEntriesTotal(totalHours);
          setTimeEntriesSource('own');
          if (!formData.actual_hours || formData.actual_hours === 0) {
            onDataChange('actual_hours', totalHours);
          }
        }
      }
    } catch (error) {
      console.error('Error loading time entries:', error);
    } finally {
      setLoadingTimeEntries(false);
      markInitialOverviewLoadComplete('timeEntries');
    }
  }, [formData.actual_hours, getAuthToken, hasPreloadedTimeSummary, markInitialOverviewLoadComplete, onDataChange, workItem?.id, workItem.item_type]);

  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    loadContacts();
    if (shouldLoadInitialTimeEntries) {
      void loadTimeEntries();
    } else {
      markInitialOverviewLoadComplete('timeEntries');
    }
  }, [initialDataLoading, loadContacts, loadTimeEntries, markInitialOverviewLoadComplete, shouldLoadInitialTimeEntries]);

  // Load locations when contact changes
  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    if (resolvedContactId) {
      if (lastLoadedContactRelationshipsRef.current === resolvedContactId) {
        return;
      }

      lastLoadedContactRelationshipsRef.current = resolvedContactId;
      loadLocationsRef.current?.(resolvedContactId);
      loadContactPeopleRef.current?.(resolvedContactId);
    } else {
      lastLoadedContactRelationshipsRef.current = null;
      latestLocationLoadRequestId.current += 1;
      latestPeopleLoadRequestId.current += 1;
      setLocations([]);
      setContactPeople([]);
      setSelectedPerson(null);
      markInitialOverviewLoadComplete('locations');
      markInitialOverviewLoadComplete('people');
    }
  }, [initialDataLoading, markInitialOverviewLoadComplete, resolvedContactId]);

  const effectiveContacts = useMemo<Contact[]>(() => {
    if (!contactDetails?.company) {
      return contacts;
    }

    const detailCompany = contactDetails.company;
    if (contacts.some((contact) => Number(contact.id) === Number(detailCompany.id))) {
      return contacts;
    }

    return [detailCompany, ...contacts];
  }, [contactDetails?.company, contacts]);

  const effectiveLocations = useMemo<Location[]>(() => {
    const detailLocation = contactDetails?.location;
    if (!detailLocation) {
      return locations;
    }

    if (locations.some((location) => Number(location.id) === Number(detailLocation.id))) {
      return locations;
    }

    return [
      {
        id: detailLocation.id,
        contact_id: resolvedContactId ?? 0,
        location_name: detailLocation.location_name,
        address_line1: detailLocation.address ?? null,
        address_line2: null,
        city: null,
        state: null,
        postal_code: null,
        contact_person: detailLocation.contact_person ?? null,
        contact_email: detailLocation.contact_email ?? null,
        contact_phone: detailLocation.contact_phone ?? null,
        is_primary: false,
      },
      ...locations,
    ];
  }, [contactDetails?.location, locations, resolvedContactId]);

  const effectiveContactPeople = useMemo<ContactPerson[]>(() => {
    if (!contactDetails?.people?.length) {
      return contactPeople;
    }

    const merged = [...contactPeople];
    contactDetails.people.forEach((person) => {
      if (!merged.some((existingPerson) => Number(existingPerson.id) === Number(person.id))) {
        merged.push(person);
      }
    });

    return merged;
  }, [contactDetails?.people, contactPeople]);

  // Auto-select location if none is set but locations are loaded
  useEffect(() => {
    if (effectiveLocations.length === 0 || loadingLocations) {
      return;
    }

    if (resolvedLocationId) {
      // Location already set — just ensure selectedLocationWorkItemId is correct
      const loc = effectiveLocations.find((l) => Number(l.id) === Number(resolvedLocationId));
      if (loc && !selectedLocationWorkItemId) {
        setSelectedLocationWorkItemId(loc.work_item_id || resolveStakeholderWorkItemId('location', loc.id));
      }
      return;
    }

    // No location set — try to auto-select primary or first location
    const primaryLocation = effectiveLocations.find((l) => l.is_primary) || effectiveLocations[0];
    if (primaryLocation) {
      onDataChange('location_id', primaryLocation.id);
      onDataChange('location_name', primaryLocation.location_name || null);
      setSelectedLocationWorkItemId(primaryLocation.work_item_id || resolveStakeholderWorkItemId('location', primaryLocation.id));
    }
  }, [effectiveLocations, loadingLocations, onDataChange, resolvedLocationId, selectedLocationWorkItemId]);

  // Load selected person details OR auto-select if only one person
  useEffect(() => {
    if (effectiveContactPeople.length === 0) {
      setSelectedPerson(null);
      return;
    }

    if (resolvedContactPersonId) {
      // Restore previously saved person
      const person = effectiveContactPeople.find((entry) => Number(entry.id) === Number(resolvedContactPersonId));
      setSelectedPerson(person || null);
    } else if (resolvedContactPersonName) {
      const normalizedSelectedName = resolvedContactPersonName.toLowerCase();
      const person = effectiveContactPeople.find((entry) => (
        `${entry.first_name} ${entry.last_name}`.trim().toLowerCase() === normalizedSelectedName
      ));
      setSelectedPerson(person || null);
    } else if (effectiveContactPeople.length === 1) {
      // Auto-select the only available person
      const person = effectiveContactPeople[0];
      const nextPersonName = `${person.first_name} ${person.last_name}`;
      const currentPersonName = resolvedContactPersonName;

      if (resolvedContactPersonId !== person.id) {
        onDataChange('contact_person_id', person.id);
      }

      if (currentPersonName !== nextPersonName) {
        onDataChange('contact_person', nextPersonName);
      }

      setSelectedPerson(person);
    } else {
      setSelectedPerson(null);
    }
  }, [effectiveContactPeople, onDataChange, resolvedContactPersonId, resolvedContactPersonName]);

  useEffect(() => {
    const fallbackContactId = normalizePositiveIntegerId(contactDetails?.company?.id);
    if (resolvedContactId == null && fallbackContactId != null) {
      onDataChange('contact_id', fallbackContactId);
      if (!(formData.contact_name ?? workItem.contact_name)) {
        onDataChange('contact_name', contactDetails?.company?.company_name || contactDetails?.company?.display_name || contactDetails?.company?.contact_person || null);
      }
    }

    const fallbackLocationId = normalizePositiveIntegerId(contactDetails?.location?.id);
    if (resolvedLocationId == null && fallbackLocationId != null) {
      onDataChange('location_id', fallbackLocationId);
      if (!(formData.location_name ?? workItem.location_name)) {
        onDataChange('location_name', contactDetails?.location?.location_name || null);
      }
    }

    if (resolvedLocationId == null) {
      const persistedLocationName = String(formData.location_name ?? workItem.location_name ?? '').trim().toLowerCase();
      if (persistedLocationName) {
        const fallbackLocation = effectiveLocations.find((location) => String(location.location_name || '').trim().toLowerCase() === persistedLocationName) || null;

        if (fallbackLocation) {
          onDataChange('location_id', fallbackLocation.id);
          if (!(formData.location_name ?? workItem.location_name)) {
            onDataChange('location_name', fallbackLocation.location_name || null);
          }
          setSelectedLocationWorkItemId(fallbackLocation.work_item_id || resolveStakeholderWorkItemId('location', fallbackLocation.id));
        }
      }
    }

    if (resolvedContactPersonId == null) {
      const normalizedSelectedName = resolvedContactPersonName.trim().toLowerCase();
      const fallbackPerson = effectiveContactPeople.find((person) => {
        if (normalizedSelectedName) {
          return `${person.first_name} ${person.last_name}`.trim().toLowerCase() === normalizedSelectedName;
        }

        return false;
      }) || null;

      if (fallbackPerson) {
        onDataChange('contact_person_id', fallbackPerson.id);
        if (!resolvedContactPersonName) {
          onDataChange('contact_person', `${fallbackPerson.first_name} ${fallbackPerson.last_name}`.trim());
        }
      }
    }
  }, [
    contactDetails,
    effectiveContactPeople,
    effectiveLocations,
    formData.contact_name,
    formData.location_name,
    onDataChange,
    resolvedContactId,
    resolvedContactPersonId,
    resolvedContactPersonName,
    resolvedLocationId,
    workItem.contact_name,
    workItem.location_name,
  ]);

  useEffect(() => {
    if (loadingLocations) {
      return;
    }

    const normalizedLocationId = normalizePositiveIntegerId(formData.location_id);
    if (normalizedLocationId && !effectiveLocations.some((location) => Number(location.id) === normalizedLocationId)) {
      onDataChange('location_id', null);
      onDataChange('location_name', null);
    }
  }, [effectiveLocations, formData.location_id, loadingLocations, onDataChange]);

  useEffect(() => {
    if (loadingPeople) {
      return;
    }

    const normalizedPersonId = normalizePositiveIntegerId(formData.contact_person_id);
    if (normalizedPersonId && !effectiveContactPeople.some((person) => Number(person.id) === normalizedPersonId)) {
      onDataChange('contact_person_id', null);
      onDataChange('contact_person', null);
      setSelectedPerson(null);
      return;
    }

    const extraPersonEntries: Array<['contact_person_id2' | 'contact_person_id3', number | null]> = [
      ['contact_person_id2', resolvedContactPersonId2],
      ['contact_person_id3', resolvedContactPersonId3],
    ];
    for (const [extraField, extraPersonId] of extraPersonEntries) {
      if (extraPersonId && !effectiveContactPeople.some((person) => Number(person.id) === extraPersonId)) {
        onDataChange(extraField, null);
      }
    }

    if (selectedPerson && !effectiveContactPeople.some((person) => Number(person.id) === Number(selectedPerson.id))) {
      setSelectedPerson(null);
    }
  }, [effectiveContactPeople, formData.contact_person_id, resolvedContactPersonId2, resolvedContactPersonId3, loadingPeople, onDataChange, selectedPerson]);

  const handleContactChange = (contactId: number | null, contactName?: string) => {
    const normalizedContactId = normalizePositiveIntegerId(contactId);

    setSelectedContactWorkItemId(resolveStakeholderWorkItemId('contact', normalizedContactId));
    setSelectedLocationWorkItemId(null);
    setSelectedContactPersonWorkItemId(null);
    onDataChange('contact_id', normalizedContactId);
    onDataChange('contact_name', contactName || null);
    // Clear dependent fields
    onDataChange('location_id', null);
    onDataChange('location_name', null);
    onDataChange('contact_person_id', null);
    onDataChange('contact_person_id2', null);
    onDataChange('contact_person_id3', null);
    onDataChange('contact_person', null);

    // Immediately clear stale local state so dropdowns don't show old contact's data
    if (activeContactIdRef.current !== normalizedContactId) {
      setLocations([]);
      setContactPeople([]);
      setContactDetails(null);
      setSelectedPerson(null);
      // Mark loading as true so the stale-data cleanup useEffects (which clear
      // location_id / contact_person_id when they're not in effectiveLocations /
      // effectiveContactPeople) don't fire before the new contact's data loads.
      setLoadingLocations(true);
      setLoadingPeople(true);
      lastLoadedContactRelationshipsRef.current = null;
      lastLoadedContactDetailsKeyRef.current = '';
    }

    // Reset location source to contact mode
    setLocationSource('contact');
    setCustomLocation(null);
    setCustomLocSearch('');

    // Warn (do not gate) if the selected contact has an account-standing issue.
    if (normalizedContactId) {
      void import('@/lib/contacts/accountWarning').then(({ warnIfContactAccountIssue }) => {
        void warnIfContactAccountIssue({ contactId: normalizedContactId, action: 'attach this contact' });
      });
    }
  };

  const handleLocationChange = (locationId: number | null) => {
    // Use loose equality to handle both string and number
    const location = effectiveLocations.find(l => l.id == locationId);
    setSelectedLocationWorkItemId(location?.work_item_id || resolveStakeholderWorkItemId('location', locationId));
    onDataChange('location_id', locationId);
    onDataChange('location_name', location?.location_name || null);
    const addressParts = [
      location?.address_line1,
      location?.address_line2,
      location?.city,
      location?.state,
      location?.postal_code,
    ].filter(Boolean);
    onDataChange('location_address', addressParts.length > 0 ? addressParts.join(', ') : null);
  };

  const handlePersonChange = (personId: number | null) => {
    setSelectedContactPersonWorkItemId(resolveStakeholderWorkItemId('person', personId));
    onDataChange('contact_person_id', personId);
    onDataChange('contact_person_id2', null);
    onDataChange('contact_person_id3', null);
    // Use loose equality to handle both string and number
    const person = effectiveContactPeople.find(p => p.id == personId);
    onDataChange('contact_person', person ? `${person.first_name} ${person.last_name}` : null);
    setSelectedPerson(person || null);
  };

  // Multi-person selection — ids[0] is the primary person persisted in
  // contact_person_id; ids[1]/ids[2] map to contact_person_id2/3.
  const handlePersonsChange = (ids: number[], names: string[]) => {
    const [primaryId, secondId, thirdId] = ids;
    setSelectedContactPersonWorkItemId(resolveStakeholderWorkItemId('person', primaryId ?? null));
    onDataChange('contact_person_id', primaryId ?? null);
    onDataChange('contact_person_id2', secondId ?? null);
    onDataChange('contact_person_id3', thirdId ?? null);
    const primaryPerson = effectiveContactPeople.find(p => p.id == primaryId);
    onDataChange('contact_person', names[0] || (primaryPerson ? `${primaryPerson.first_name} ${primaryPerson.last_name}` : null));
    setSelectedPerson(primaryPerson || null);
  };

  const handleUnifiedSelect = (result: UnifiedLookupSearchResult) => {
    setSelectedContactWorkItemId(result.contact_work_item_id || resolveStakeholderWorkItemId('contact', result.contact_id));
    setSelectedLocationWorkItemId(result.location_work_item_id || resolveStakeholderWorkItemId('location', result.location_id));
    setSelectedContactPersonWorkItemId(result.person_work_item_id || resolveStakeholderWorkItemId('person', result.person_id));
    handleContactChange(result.contact_id, result.contact_company_name || result.contact_name);

    // handleContactChange clears selectedLocationWorkItemId and
    // selectedContactPersonWorkItemId — re-set them so the 3-way selector
    // correctly reflects the new selection.
    setSelectedLocationWorkItemId(result.location_work_item_id || resolveStakeholderWorkItemId('location', result.location_id));
    setSelectedContactPersonWorkItemId(result.person_work_item_id || resolveStakeholderWorkItemId('person', result.person_id));

    if (result.location_id) {
      onDataChange('location_id', result.location_id);
      onDataChange('location_name', result.location_name);
      onDataChange('location_address', result.location_address || null);
    }

    if (result.person_id) {
      // Same contact → append the person to the multi-selection (max 3);
      // different contact → handleContactChange already cleared the set.
      const personId = Number(result.person_id);
      const sameContact = result.contact_id != null && resolvedContactId != null
        && Number(result.contact_id) === Number(resolvedContactId);
      const ids = sameContact
        ? [resolvedContactPersonId, resolvedContactPersonId2, resolvedContactPersonId3].filter((id): id is number => id != null)
        : [];
      if (!ids.includes(personId) && ids.length < 3) {
        ids.push(personId);
      }
      handlePersonsChange(ids, ids.map((id) => (id === personId ? result.person_name || '' : '')));
    }
  };

  const sharedContacts = useMemo<UnifiedLookupContact[]>(() => (
    effectiveContacts.map((contact) => ({
      id: contact.id,
      work_item_id: contact.work_item_id || resolveStakeholderWorkItemId('contact', contact.id) || undefined,
      company_name: contact.company_name ?? null,
      contact_person: contact.contact_person ?? null,
      email: contact.email ?? null,
      phone: contact.phone ?? null,
      contact_type: contact.contact_type,
      account_status: contact.account_status ?? null,
      is_active: contact.is_active ?? null,
      payment_terms: contact.payment_terms ?? null,
      payment_terms_days: contact.payment_terms_days ?? null,
      credit_limit: contact.credit_limit ?? null,
      current_balance: contact.current_balance ?? null,
      currency: contact.currency ?? null,
    }))
  ), [effectiveContacts]);

  const sharedLocations = useMemo<UnifiedLookupLocation[]>(() => (
    effectiveLocations.map((location) => ({
      id: location.id,
      work_item_id: location.work_item_id || resolveStakeholderWorkItemId('location', location.id) || undefined,
      contact_id: location.contact_id,
      contact_work_item_id: location.contact_work_item_id || resolveStakeholderWorkItemId('contact', location.contact_id),
      location_name: location.location_name,
      address_line1: location.address_line1 ?? null,
      address_line2: location.address_line2 ?? null,
      city: location.city ?? null,
      state: location.state ?? null,
      postal_code: location.postal_code ?? null,
      country: null,
      contact_person: location.contact_person ?? null,
      contact_email: location.contact_email ?? null,
      contact_phone: location.contact_phone ?? null,
      is_primary: Boolean(location.is_primary),
    }))
  ), [effectiveLocations]);

  const sharedContactPeople = useMemo<UnifiedLookupContactPerson[]>(() => (
    effectiveContactPeople.map((person) => ({
      id: person.id,
      work_item_id: person.work_item_id || resolveStakeholderWorkItemId('person', person.id) || undefined,
      contact_id: person.contact_id,
      contact_work_item_id: person.contact_work_item_id || resolveStakeholderWorkItemId('contact', person.contact_id),
      first_name: person.first_name,
      last_name: person.last_name,
      job_title: person.job_title ?? null,
      email: person.email ?? null,
      phone: person.phone ?? null,
      mobile: person.mobile ?? null,
      is_primary: Boolean(person.is_primary),
      default_location_id: null,
      default_location_work_item_id: person.default_location_work_item_id || null,
      default_location_name: null,
      contact_company_name: null,
    }))
  ), [effectiveContactPeople]);

  const handleSharedLocationCreated = (newLocation: UnifiedLookupLocation) => {
    const createdLocation: Location = {
      id: newLocation.id,
      work_item_id: newLocation.work_item_id || resolveStakeholderWorkItemId('location', newLocation.id) || undefined,
      contact_id: newLocation.contact_id,
      contact_work_item_id: newLocation.contact_work_item_id || resolveStakeholderWorkItemId('contact', newLocation.contact_id),
      location_name: newLocation.location_name,
      address_line1: newLocation.address_line1,
      address_line2: newLocation.address_line2,
      city: newLocation.city,
      state: newLocation.state,
      postal_code: newLocation.postal_code,
      contact_person: newLocation.contact_person,
      contact_email: newLocation.contact_email,
      contact_phone: newLocation.contact_phone,
      is_primary: newLocation.is_primary,
    };

    setLocations((previous) => [createdLocation, ...previous.filter((location) => location.id !== createdLocation.id)]);
    setSelectedLocationWorkItemId(createdLocation.work_item_id || resolveStakeholderWorkItemId('location', createdLocation.id));
    handleLocationChange(createdLocation.id);
  };

  const handleSharedPersonCreated = (newPerson: UnifiedLookupContactPerson) => {
    const createdPerson: ContactPerson = {
      id: newPerson.id,
      work_item_id: newPerson.work_item_id || resolveStakeholderWorkItemId('person', newPerson.id) || undefined,
      contact_id: newPerson.contact_id,
      contact_work_item_id: newPerson.contact_work_item_id || resolveStakeholderWorkItemId('contact', newPerson.contact_id),
      first_name: newPerson.first_name,
      last_name: newPerson.last_name,
      job_title: newPerson.job_title ?? undefined,
      email: newPerson.email ?? undefined,
      phone: newPerson.phone ?? undefined,
      mobile: newPerson.mobile ?? undefined,
      is_primary: newPerson.is_primary,
      default_location_work_item_id: newPerson.default_location_work_item_id || null,
    };

    setContactPeople((previous) => [createdPerson, ...previous.filter((person) => person.id !== createdPerson.id)]);
    setSelectedContactPersonWorkItemId(createdPerson.work_item_id || resolveStakeholderWorkItemId('person', createdPerson.id));
    const personIds = [resolvedContactPersonId, resolvedContactPersonId2, resolvedContactPersonId3]
      .filter((id): id is number => id != null);
    if (!personIds.includes(Number(createdPerson.id)) && personIds.length < 3) {
      personIds.push(createdPerson.id);
    }
    handlePersonsChange(
      personIds,
      personIds.map((id) => (Number(id) === Number(createdPerson.id) ? `${createdPerson.first_name} ${createdPerson.last_name}`.trim() : '')),
    );
  };

  const normalizePhoneValue = useCallback((value?: string | null) => {
    if (!value) return '';
    return value.replace(/[^\d+]/g, '');
  }, []);

  const buildWhatsAppHref = useCallback((value?: string | null) => {
    const normalized = normalizePhoneValue(value);
    if (!normalized) return null;

    const digits = normalized.startsWith('+') ? normalized.slice(1) : normalized.replace(/^0+/, '');
    return digits ? `https://wa.me/${digits}` : null;
  }, [normalizePhoneValue]);

  const loadSelectedContactDetails = useCallback(async () => {
    if (!resolvedContactId) {
      setContactDetails(null);
      markInitialOverviewLoadComplete('contactDetails');
      return;
    }

    setLoadingContactDetails(true);
    try {
      let company: Contact | undefined;
      let people: ContactPerson[] = [];
      let location: ContactDetailsState['location'];

      if (contextLoadContactById && contextLoadContactPeople && contextLoadContactLocationById) {
        const [companyData, peopleData, locationData] = await Promise.all([
          contextLoadContactById(resolvedContactId),
          contextLoadContactPeople(resolvedContactId),
          resolvedLocationId ? contextLoadContactLocationById(resolvedLocationId) : Promise.resolve(null),
        ]);

        if (companyData) {
          const detail = companyData as ContactSummary;
          company = {
            id: detail.id,
            work_item_id: (detail as ContactSummary & { work_item_id?: string }).work_item_id
              || resolveStakeholderWorkItemId('contact', detail.id)
              || undefined,
            company_name: detail.company_name ?? detail.display_name ?? detail.contact_person ?? null,
            display_name: detail.display_name ?? null,
            contact_person: detail.contact_person ?? null,
            email: detail.email ?? null,
            phone: detail.phone ?? null,
            mobile: detail.mobile ?? null,
            contact_type: detail.contact_type || 'customer',
            account_status: detail.account_status ?? null,
            is_active: detail.is_active ?? null,
            payment_terms: detail.payment_terms ?? null,
            payment_terms_days: detail.payment_terms_days ?? null,
            credit_limit: detail.credit_limit ?? null,
            current_balance: detail.current_balance ?? null,
            currency: detail.currency ?? null,
          };
        }
        people = (peopleData as ContactPerson[]) || [];

        if (locationData) {
          const detail = locationData as ContactLocationDetail;
          const addressParts = [detail.address_line1, detail.address_line2, detail.city, detail.state, detail.postal_code].filter(Boolean);
          location = {
            id: detail.id,
            location_name: detail.location_name,
            contact_person: detail.contact_person,
            contact_email: detail.contact_email,
            contact_phone: detail.contact_phone,
            address: addressParts.length > 0 ? addressParts.join(', ') : null,
          };
        }
      } else {
        const headers = { 'Authorization': `Bearer ${getAuthToken()}` };
        const [companyRes, nextPeople, locationRes] = await Promise.all([
          fetch(`/api/v1/contacts/${resolvedContactId}`, { headers }),
          fetchContactPeople<ContactPerson>({
            contactId: resolvedContactId,
            token: getAuthToken(),
          }),
          resolvedLocationId ? fetch(`/api/v1/contacts/locations/${resolvedLocationId}`, { headers }) : Promise.resolve(null),
        ]);

        if (companyRes.ok) {
          const data = await companyRes.json();
          if (data.success && data.data) {
            company = data.data;
          }
        }

        people = nextPeople || [];

        if (locationRes && locationRes.ok) {
          const data = await locationRes.json();
          if (data.success && data.data) {
            const detail = data.data;
            const addressParts = [detail.address_line1, detail.address_line2, detail.city, detail.state, detail.postal_code].filter(Boolean);
            location = {
              id: detail.id,
              location_name: detail.location_name,
              contact_person: detail.contact_person,
              contact_email: detail.contact_email,
              contact_phone: detail.contact_phone,
              address: addressParts.length > 0 ? addressParts.join(', ') : null,
            };
          }
        }
      }

      setContactDetails({ company, people, location });
    } catch (error) {
      console.error('Error loading selected contact details:', error);
      setContactDetails(null);
    } finally {
      setLoadingContactDetails(false);
      markInitialOverviewLoadComplete('contactDetails');
    }
  }, [contextLoadContactById, contextLoadContactLocationById, contextLoadContactPeople, getAuthToken, markInitialOverviewLoadComplete, resolvedContactId, resolvedLocationId]);

  useEffect(() => {
    if (initialDataLoading) {
      return;
    }

    if (resolvedContactId) {
      const detailsKey = `${resolvedContactId}:${resolvedLocationId ?? 'none'}`;
      if (lastLoadedContactDetailsKeyRef.current === detailsKey) {
        return;
      }

      lastLoadedContactDetailsKeyRef.current = detailsKey;
      loadSelectedContactDetails();
    } else {
      lastLoadedContactDetailsKeyRef.current = '';
      setContactDetails(null);
      markInitialOverviewLoadComplete('contactDetails');
    }
  }, [initialDataLoading, loadSelectedContactDetails, markInitialOverviewLoadComplete, resolvedContactId, resolvedLocationId]);

  // Use loose equality to handle both string and number contact_id (bigint from DB serializes as string)
  const selectedContact = effectiveContacts.find((contact) => (
    (selectedContactWorkItemId && contact.work_item_id === selectedContactWorkItemId)
    || contact.id == resolvedContactId
  )) || contactDetails?.company;
  const selectedLocation = effectiveLocations.find((location) => (
    (selectedLocationWorkItemId && location.work_item_id === selectedLocationWorkItemId)
    || location.id == resolvedLocationId
  ));
  const resolvedContactPerson = useMemo(() => {
    const normalizedSelectedName = resolvedContactPersonName.toLowerCase();

    return effectiveContactPeople.find((person) => {
      if (selectedContactPersonWorkItemId && person.work_item_id === selectedContactPersonWorkItemId) {
        return true;
      }

      if (resolvedContactPersonId != null && person.id == resolvedContactPersonId) {
        return true;
      }

      if (!normalizedSelectedName) {
        return false;
      }

      return `${person.first_name} ${person.last_name}`.trim().toLowerCase() === normalizedSelectedName;
    }) || effectiveContactPeople.find((person) => person.is_primary) || null;
  }, [effectiveContactPeople, resolvedContactPersonId, resolvedContactPersonName, selectedContactPersonWorkItemId]);

  const resolvedCompanyName = selectedContact?.company_name || selectedContact?.display_name || selectedContact?.contact_person || formData.contact_name || workItem.contact_name || 'Contact';
  const resolvedPersonName = resolvedContactPerson
    ? `${resolvedContactPerson.first_name} ${resolvedContactPerson.last_name}`.trim()
    : resolvedContactPersonName || null;
  const resolvedPersonRole = resolvedContactPerson
    ? [resolvedContactPerson.job_title, resolvedContactPerson.department].filter(Boolean).join(' · ')
    : null;
  const resolvedPersonPhone = resolvedContactPerson?.mobile || resolvedContactPerson?.phone || resolvedContactPerson?.direct_line || null;
  const resolvedPersonEmail = resolvedContactPerson?.email || null;
  const resolvedLocationName = selectedLocation?.location_name
    || contactDetails?.location?.location_name
    || formData.location_name
    || workItem.location_name
    || null;
  const resolvedLocationAddress = contactDetails?.location?.address
    || [selectedLocation?.address_line1, selectedLocation?.address_line2, selectedLocation?.city, selectedLocation?.state, selectedLocation?.postal_code].filter(Boolean).join(', ')
    || null;
  const resolvedLocationContactName = contactDetails?.location?.contact_person || null;
  const resolvedLocationContactPhone = contactDetails?.location?.contact_phone || null;
  const resolvedLocationContactEmail = contactDetails?.location?.contact_email || null;
  const primaryPhone = resolvedPersonPhone || resolvedLocationContactPhone || selectedContact?.mobile || selectedContact?.phone || formData.contact_phone || workItem.contact_phone || null;
  const primaryEmail = resolvedPersonEmail || resolvedLocationContactEmail || selectedContact?.email || formData.contact_email || workItem.contact_email || null;
  const whatsappHref = buildWhatsAppHref(primaryPhone);
  const locationWhatsappHref = buildWhatsAppHref(resolvedLocationContactPhone);
  const accountStandingSnapshot = useMemo(() => {
    if (!selectedContact) {
      return null;
    }

    return calculateContactAccountStanding(selectedContact, 0, workItem.item_type || 'job');
  }, [selectedContact, workItem.item_type]);
  const accountStandingTone = accountStandingSnapshot?.currentStanding.status === 'blocked'
    ? 'red'
    : accountStandingSnapshot?.currentStanding.status === 'warning'
      ? 'amber'
      : 'green';
  const formatCurrencyAmount = useCallback((amount: number, currency = 'ZAR') => {
    try {
      return new Intl.NumberFormat('en-ZA', { style: 'currency', currency }).format(amount);
    } catch {
      return `${currency} ${amount.toFixed(2)}`;
    }
  }, []);
  const hasActionableContactDetails = Boolean(
    selectedContact
    || selectedLocation
    || resolvedPersonName
    || primaryPhone
    || primaryEmail
    || resolvedLocationAddress
  );
  const hasAppointmentContactSummary = Boolean(selectedContact || resolvedLocationName || resolvedPersonName);

  const jobCostContextSummary = useMemo(() => {
    const workItemRecord = workItem as WorkItem & Record<string, unknown>;

    const costCentre = getFirstNonEmptyString([
      formData.cost_center,
      formData.cost_centre,
      mergedCustomFields.cost_center,
      mergedCustomFields.cost_centre,
      mergedCustomFields.default_cost_center,
      workItemRecord.cost_center,
      workItemRecord.cost_centre,
      workItemRecord.cost_center_name,
      workItemRecord.cost_centre_name,
    ]);

    const departmentName = getFirstNonEmptyString([
      formData.department,
      mergedCustomFields.department,
      mergedCustomFields.department_name,
      resolvedContactPerson?.department,
      selectedContact?.contact_type === 'supplier' ? 'Supplier managed' : null,
    ]);

    const workOrderNumber = getFirstNonEmptyString([
      mergedCustomFields.work_order_number,
      mergedCustomFields.work_order_item_number,
      mergedCustomFields.source_work_order_number,
      workItemRecord.work_order_number,
      workItem.item_type === 'work_order' ? workItem.item_number : null,
    ]);

    const workOrderId = getFirstPositiveIntegerFromValues([
      mergedCustomFields.work_order_id,
      mergedCustomFields.source_work_order_id,
      workItemRecord.work_order_id,
      workItem.item_type === 'work_order' ? workItem.id : null,
    ]);

    const workOrderStatus = formatContextStatusLabel(getFirstNonEmptyString([
      mergedCustomFields.work_order_status,
      mergedCustomFields.source_work_order_status,
      workItemRecord.work_order_status,
      workItem.item_type === 'work_order' ? workItem.status : null,
    ]));

    const billingSubmissionNumber = getFirstNonEmptyString([
      mergedCustomFields.billing_submission_number,
      mergedCustomFields.request_billing_number,
      mergedCustomFields.billing_request_number,
      workItemRecord.billing_submission_number,
      workItem.item_type === 'billing_submission' ? workItem.item_number : null,
    ]);

    const billingSubmissionId = getFirstPositiveIntegerFromValues([
      mergedCustomFields.billing_submission_id,
      mergedCustomFields.request_billing_id,
      workItemRecord.billing_submission_id,
      workItem.item_type === 'billing_submission' ? workItem.id : null,
    ]);

    const billingSubmissionStatus = formatContextStatusLabel(getFirstNonEmptyString([
      mergedCustomFields.billing_submission_status,
      mergedCustomFields.request_billing_status,
      mergedCustomFields.billing_request_status,
      workItemRecord.billing_submission_status,
      workItem.item_type === 'billing_submission' ? workItem.status : null,
    ]));

    const purchaseOrderCount = getReferenceCount([
      mergedCustomFields.purchase_order_ids,
      mergedCustomFields.purchase_orders,
      mergedCustomFields.purchase_order_numbers,
      mergedCustomFields.purchase_order_id,
      mergedCustomFields.purchase_order_number,
      workItemRecord.purchase_order_ids,
      workItem.item_type === 'purchase_order' ? workItem.id : null,
    ]);

    const purchaseOrderStatus = formatContextStatusLabel(getFirstNonEmptyString([
      mergedCustomFields.purchase_order_status,
      mergedCustomFields.procurement_status,
      workItemRecord.purchase_order_status,
      workItem.item_type === 'purchase_order' ? workItem.status : null,
    ]));

    const blockers: string[] = [];
    if (!resolvedContactId) {
      blockers.push('Customer/contact is not linked yet.');
    }
    if (!resolvedLocationName) {
      blockers.push('Site/location context is still missing.');
    }
    if (!costCentre) {
      blockers.push('Cost centre has not been captured yet.');
    }
    if (!billingSubmissionId && !billingSubmissionNumber) {
      blockers.push('Request Billing has not been created yet.');
    }

    const readinessTone: 'green' | 'amber' | 'red' = blockers.length >= 3
      ? 'red'
      : blockers.length > 0
        ? 'amber'
        : 'green';

    const readinessLabel = readinessTone === 'green'
      ? 'Ready for downstream costing and finance handoff'
      : readinessTone === 'amber'
        ? 'Context is usable, but a few costing anchors still need attention'
        : 'Core job-cost context is incomplete';

    const nextStep = !resolvedContactId || !resolvedLocationName
      ? 'Finish the overview linkage so costing, procurement, and billing all reference the same customer/site context.'
      : !billingSubmissionId && !billingSubmissionNumber
        ? 'Once source costs are ready, send this job to Request Billing so finance receives a stable handoff.'
        : purchaseOrderCount > 0 && !purchaseOrderStatus
          ? 'Monitor procurement commitments here so supplier-side costs can roll back into the job cleanly.'
          : 'Use the costing and operations tabs to monitor billable progress, procurement, and close-out readiness.';

    return {
      costCentre,
      departmentName,
      workOrderNumber,
      workOrderId,
      workOrderStatus,
      billingSubmissionNumber,
      billingSubmissionId,
      billingSubmissionStatus,
      purchaseOrderCount,
      purchaseOrderStatus,
      readinessTone,
      readinessLabel,
      blockers,
      nextStep,
    };
  }, [formData.cost_center, formData.cost_centre, formData.department, mergedCustomFields, resolvedContactId, resolvedContactPerson?.department, resolvedLocationName, workItem, selectedContact]);
  const tabClasses = (active: boolean) => `flex-1 flex items-center justify-center gap-1.5 px-2 py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors ${active
    ? isDark ? 'bg-blue-600 text-white' : 'bg-blue-500 text-white'
    : isDark ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
    }`;

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '';
    return formatDateTimeSAST(dateString);
  };

  const formatMinutesCompact = useCallback((minutes?: number | null) => {
    if (!Number.isFinite(minutes) || minutes == null) return '—';

    const rounded = Math.abs(Math.round(minutes));
    const hours = Math.floor(rounded / 60);
    const remainder = rounded % 60;

    if (hours > 0 && remainder > 0) {
      return `${hours}h ${remainder}m`;
    }

    if (hours > 0) {
      return `${hours}h`;
    }

    return `${rounded}m`;
  }, []);

  const publishAppointmentCompletionOverrideUpdate = useCallback(async (
    nextOverride: Record<string, unknown> | null,
  ) => {
    const rawClientId = workItem.client_id ?? currentUser?.client_id;
    const numericClientId = typeof rawClientId === 'number' ? rawClientId : Number(rawClientId);

    if (!Number.isFinite(numericClientId) || numericClientId <= 0 || !workItem.id) {
      return;
    }

    const event = buildOperationsRealtimeEvent({
      eventType: 'appointment_completion_override_changed',
      clientId: numericClientId,
      entityType: 'appointment',
      entityId: String(workItem.id),
      actorUserId: currentUser?.id ?? null,
      source: 'work_item_detail_modal',
      sourceModule: 'unified_overview',
      payload: {
        client_id: numericClientId,
        work_item_id: String(workItem.id),
        appointment_id: String(workItem.id),
        appointment_ids: [String(workItem.id)],
        parent_job_id: appointmentParentJobId,
        assignee_user_ids: appointmentOverrideAssigneeUserIds,
        completion_rules_override: nextOverride,
        completion_rules_override_enabled: Boolean(nextOverride),
        emitted_at: new Date().toISOString(),
      },
    });

    const channels = new Set<string>([`operations:client:${numericClientId}`]);
    appointmentOverrideAssigneeUserIds.forEach((userId) => {
      channels.add(`tasks:user:${userId}:client:${numericClientId}`);
    });

    if (wsClient.getStatus() === 'disconnected') {
      try {
        await wsClient.connect();
      } catch (error) {
        console.warn('[UnifiedOverviewTab] Unable to connect websocket for completion override broadcast:', error);
      }
    }

    await Promise.all(Array.from(channels).map((channel) => wsClient.publish(channel, event)));
  }, [appointmentOverrideAssigneeUserIds, appointmentParentJobId, currentUser?.client_id, currentUser?.id, workItem.client_id, workItem.id]);

  const persistAppointmentCompletionOverride = useCallback(async (nextEnabled: boolean) => {
    if (workItem.item_type !== 'appointment' || !canManageAppointmentCompletionOverride || !workItem.id) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      notify.error('You need to be signed in to update the completion override.');
      return;
    }

    const trimmedReason = appointmentCompletionOverrideForm.reason.trim();
    if (nextEnabled && trimmedReason.length < APPOINTMENT_COMPLETION_OVERRIDE_REASON_MIN_LENGTH) {
      return;
    }

    try {
      setSavingAppointmentCompletionOverride(true);

      const nextCustomFields = { ...mergedCustomFields };
      let nextOverridePayload: Record<string, unknown> | null = null;

      if (!nextEnabled) {
        delete nextCustomFields.completion_rules_override;
      } else {
        const approvedByName = [currentUser?.first_name, currentUser?.last_name]
          .filter(Boolean)
          .join(' ')
          .trim()
          || currentUser?.email
          || 'Current user';

        nextOverridePayload = {
          enabled: true,
          reason: trimmedReason,
          approvedByUserId: currentUser?.id ?? null,
          approvedByName,
          approvedAt: new Date().toISOString(),
        };
        nextCustomFields.completion_rules_override = nextOverridePayload;
      }

      const response = await fetch(`/api/v1/work-items/${workItem.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          custom_fields: nextCustomFields,
        }),
      });

      const responseData = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(responseData?.error || 'Failed to update appointment completion override');
      }

      const persistedCustomFields = responseData?.data?.custom_fields && typeof responseData.data.custom_fields === 'object'
        ? responseData.data.custom_fields
        : nextCustomFields;
      const nextState = getAppointmentCompletionOverrideFormState(persistedCustomFields);

      onDataChange('custom_fields', persistedCustomFields);
      setAppointmentCompletionOverrideForm(nextState);
      await publishAppointmentCompletionOverrideUpdate(nextState.enabled ? (persistedCustomFields.completion_rules_override as Record<string, unknown>) : null);
      notify.success(nextState.enabled ? 'Completion override enabled' : 'Completion override removed');
    } catch (error) {
      console.error('Error saving appointment completion override:', error);
      setAppointmentCompletionOverrideForm(persistedAppointmentCompletionOverride);
      notify.error(error instanceof Error ? error.message : 'Failed to update appointment completion override');
    } finally {
      setSavingAppointmentCompletionOverride(false);
    }
  }, [appointmentCompletionOverrideForm.reason, canManageAppointmentCompletionOverride, currentUser?.email, currentUser?.first_name, currentUser?.id, currentUser?.last_name, getAuthToken, mergedCustomFields, onDataChange, persistedAppointmentCompletionOverride, publishAppointmentCompletionOverrideUpdate, workItem.id, workItem.item_type]);

  const handleSubtypeChange = useCallback(async (nextSubtypeValue: string | null) => {
    if (workItem.item_type !== 'appointment') {
      onDataChange('item_subtype', nextSubtypeValue);
      return;
    }

    const currentSubtypeKey = normalizeAppointmentTypeKey(formData.item_subtype || workItem.item_subtype || '');
    const nextSubtypeKey = normalizeAppointmentTypeKey(nextSubtypeValue || '');

    if (currentSubtypeKey === nextSubtypeKey) {
      onDataChange('item_subtype', nextSubtypeValue);
      return;
    }

    const isExistingAppointment = Boolean(workItem.id) && !String(workItem.id).startsWith('temp-');
    if (!isExistingAppointment) {
      onDataChange('item_subtype', nextSubtypeValue);
      return;
    }

    const currentTypeOption = appointmentSubtypeOptions.find(
      type => normalizeAppointmentTypeKey(type.key) === currentSubtypeKey
    );
    const nextTypeOption = appointmentSubtypeOptions.find(
      type => normalizeAppointmentTypeKey(type.key) === nextSubtypeKey
    );

    const currentTypeLabel = currentTypeOption?.label || (currentSubtypeKey ? formatSubtypeLabel(currentSubtypeKey) : 'Default appointment');
    const nextTypeLabel = nextTypeOption?.label || (nextSubtypeKey ? formatSubtypeLabel(nextSubtypeKey) : 'Default appointment');

    const nextRules = getAppointmentTypeRules(
      {
        item_type: 'appointment',
        item_subtype: nextSubtypeValue,
        custom_fields: null,
      },
      appointmentSubtypeOptions
    );

    const requirementLabels = nextRules.completionRequirements.map(
      ruleKey => APPOINTMENT_REQUIREMENT_LABELS[ruleKey]
    );
    const hiddenTabLabels = nextRules.hiddenTabs.map(
      tabKey => APPOINTMENT_TAB_LABELS[tabKey]
    );

    const confirmed = await confirm({
      title: 'Change appointment type?',
      message: [
        'This appointment has already been created.',
        `Changing it from "${currentTypeLabel}" to "${nextTypeLabel}" will apply the new appointment rules:`,
        '',
        `Completion requirements: ${formatAppointmentRuleSummary(requirementLabels, 'No extra checks')}`,
        `Hidden tabs: ${formatAppointmentRuleSummary(hiddenTabLabels, 'None')}`,
        '',
        'Are you sure you want to continue?',
      ].join('\n'),
      confirmText: 'Change type',
      cancelText: 'Keep current type',
      variant: 'warning',
      onConfirm: () => { },
    });

    if (!confirmed) {
      return;
    }

    onDataChange('item_subtype', nextSubtypeValue);
  }, [appointmentSubtypeOptions, formData.item_subtype, onDataChange, workItem.id, workItem.item_subtype, workItem.item_type]);

  const selectedComplexityPoints = useMemo(() => {
    const rawValue = formData.complexity_points ?? workItem.complexity_points;
    const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }, [formData.complexity_points, workItem.complexity_points]);

  const effortPointOptions = useMemo(() => effortConfig.point_scale, [effortConfig]);

  const selectedEffortScale = useMemo(
    () => getEffortScaleEntry(effortConfig, selectedComplexityPoints),
    [effortConfig, selectedComplexityPoints],
  );

  const effectiveEstimatedHours = useMemo(() => {
    const rawValue = formData.estimated_hours ?? workItem.estimated_hours;
    const numericValue = typeof rawValue === 'number' ? rawValue : Number(rawValue);
    return Number.isFinite(numericValue) ? numericValue : null;
  }, [formData.estimated_hours, workItem.estimated_hours]);

  const showEstimatedResults = selectedComplexityPoints !== null && selectedComplexityPoints > 0;

  const actualHours = timeEntriesTotal || formData.actual_hours || 0;

  const progressSummary = useMemo(() => {
    return deriveWorkItemProgress({
      id: workItem.id,
      item_type: workItem.item_type,
      status: formData.status ?? workItem.status,
      progress_percentage: formData.progress_percentage ?? workItem.progress_percentage,
      custom_fields: mergedCustomFields,
      children: progressChildren,
    });
  }, [formData.progress_percentage, formData.status, mergedCustomFields, progressChildren, workItem.id, workItem.item_type, workItem.progress_percentage, workItem.status]);

  const displayProgressPercentage = progressSummary.progressPercentage;
  const progressIsReadOnly = progressSummary.readOnly;
  const progressEffortReadOnly = progressIsReadOnly;
  const rollupSummary = useMemo(() => {
    if (!progressSummary.counts) {
      return null;
    }

    const { counts } = progressSummary;
    const excluded = counts.cancelled;
    const primaryValue = excluded > 0 && counts.considered > 0
      ? `${counts.completed}/${counts.considered}`
      : `${counts.completed}/${counts.total}`;

    const secondaryParts = [
      `${counts.active} active`,
      `${counts.pending} pending`,
      (counts.missed ?? 0) > 0 ? `${counts.missed} missed` : null,
      counts.cancelled > 0 ? `${counts.cancelled} cancelled` : null,
    ].filter(Boolean);

    return {
      primaryValue,
      secondaryText: secondaryParts.join(' · '),
    };
  }, [progressSummary]);

  const handleComplexityPointsChange = useCallback((value: string) => {
    const parsedPoints = value ? parseInt(value, 10) : null;
    const nextPoints = parsedPoints === null || Number.isNaN(parsedPoints) ? null : parsedPoints;

    onDataChange('complexity_points', nextPoints);

    if (effortConfig.auto_update_estimated_hours && effortConfig.calculation_mode !== 'manual' && !effortConfig.allow_manual_override) {
      const derivedHours = getEffectiveEstimatedHours(
        effortConfig,
        nextPoints,
        formData.scheduled_start,
        formData.scheduled_end,
        undefined,
        progressChildren,
      );
      onDataChange('estimated_hours', derivedHours);
    }
  }, [effortConfig, onDataChange, formData.scheduled_start, formData.scheduled_end, progressChildren]);

  useEffect(() => {
    if (loadingItemTypeConfig || !effortConfig.auto_update_estimated_hours || effortConfig.calculation_mode === 'manual' || effortConfig.allow_manual_override) {
      return;
    }

    const derivedEstimatedHours = getEffectiveEstimatedHours(
      effortConfig,
      selectedComplexityPoints,
      formData.scheduled_start,
      formData.scheduled_end,
      undefined,
      progressChildren,
    );

    if (derivedEstimatedHours === null || effectiveEstimatedHours === derivedEstimatedHours) {
      return;
    }

    onDataChange('estimated_hours', derivedEstimatedHours);
  }, [effectiveEstimatedHours, effortConfig, loadingItemTypeConfig, onDataChange, selectedComplexityPoints, formData.scheduled_start, formData.scheduled_end, progressChildren]);

  if (showInitialOverviewLoader) {
    return (
      <div className={`rounded-xl border p-8 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <BrandedLoadingSpinner
          mode="default"
          size="md"
          message="Loading overview..."
          subMessage="Preparing progress, effort, and contact details"
          primaryColor="#2563eb"
          className="py-8"
        />
      </div>
    );
  }

  return (<>
    <div className="space-y-3" data-tour-id="work-item-overview-tab">
      {/* Progress & Effort Tracker - TOP for visibility */}
      <div className={`p-2.5 sm:p-3 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`} data-tour-id="overview-progress-effort">
        <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
          <Target className="w-4 h-4 text-blue-500" />
          Progress & Effort
          {loadingProgressContext && progressIsReadOnly && (
            <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
          )}
        </h3>

        {/* Compact Progress Ring & Stats */}
        <div className="flex flex-col lg:flex-row gap-3">
          {/* Progress Ring - Smaller */}
          <div className="flex-shrink-0 flex flex-col items-center">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle cx="40" cy="40" r="34" stroke={isDark ? '#374151' : '#e5e7eb'} strokeWidth="7" fill="none" />
                <circle
                  cx="40" cy="40" r="34"
                  stroke={displayProgressPercentage === 100 ? '#10b981' : displayProgressPercentage >= 75 ? '#3b82f6' : displayProgressPercentage >= 50 ? '#f59e0b' : displayProgressPercentage >= 25 ? '#f97316' : '#ef4444'}
                  strokeWidth="7" fill="none" strokeLinecap="round"
                  strokeDasharray={`${displayProgressPercentage * 2.14} 214`}
                  className="transition-all duration-500 ease-out"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{displayProgressPercentage}%</span>
              </div>
            </div>
            <div className={`mt-1.5 text-xs font-medium text-center max-w-[9rem] leading-tight ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {progressSummary.label}
            </div>
            {progressEffortReadOnly ? (
              <div className={`mt-1.5 flex items-start gap-1.5 text-[11px] max-w-[14rem] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Lock className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{progressSummary.description}</span>
              </div>
            ) : (
              <div className="flex gap-1 mt-1.5">
                {[0, 25, 50, 75, 100].map(val => (
                  <button key={val} onClick={() => onDataChange('progress_percentage', val)}
                    className={`px-1.5 py-0.5 text-xs rounded transition-all ${displayProgressPercentage === val ? 'bg-blue-500 text-white' : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                    {val}%
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Compact Stats Grid + full complexity description (below dropdown row) */}
          <div className="flex-1 flex flex-col gap-2 min-w-0">
            <div className={`grid grid-cols-2 ${travelMinutes > 0 || totalDistanceKm > 0 ? 'lg:grid-cols-6' : 'lg:grid-cols-4'} gap-2`}>
              {/* Complexity */}
              <div className={`p-2.5 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-purple-50'} border ${isDark ? 'border-gray-700' : 'border-purple-100'}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Zap className={`w-3.5 h-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Complexity</span>
                </div>
                <select value={selectedComplexityPoints ?? ''} onChange={(e) => handleComplexityPointsChange(e.target.value)}
                  className={`text-lg font-bold bg-transparent border-none focus:ring-0 p-0 w-full ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                  <option value="">-</option>
                  {effortPointOptions.map((entry) => (
                    <option key={entry.points} value={entry.points}>{entry.points} {effortConfig.point_label} · {entry.label}</option>
                  ))}
                </select>
              </div>
              {rollupSummary && progressSummary.counts && (
                <div className={`p-2.5 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-slate-50'} border ${isDark ? 'border-gray-700' : 'border-slate-200'}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Flag className={`w-3.5 h-3.5 ${isDark ? 'text-slate-300' : 'text-slate-600'}`} />
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Roll-up</span>
                  </div>
                  <div className={`text-lg font-bold ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
                    {rollupSummary.primaryValue}
                  </div>
                  <div className={`text-[11px] mt-1 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                    {rollupSummary.secondaryText}
                  </div>
                </div>
              )}
              <div className={`p-2.5 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-blue-50'} border ${isDark ? 'border-gray-700' : 'border-blue-100'}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Clock className={`w-3.5 h-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Estimated</span>
                  {effortConfig.calculation_mode !== 'manual' && effortConfig.auto_update_estimated_hours && !effortConfig.allow_manual_override && (
                    <span title="Auto-calculated from configuration">
                      <Lock className="w-3 h-3 text-gray-400" aria-hidden="true" />
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-1">
                  {showEstimatedResults || !effortConfig.auto_update_estimated_hours ? (
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={showEstimatedResults ? (effectiveEstimatedHours ?? '') : ''}
                      onChange={(e) => onDataChange('estimated_hours', e.target.value ? parseFloat(e.target.value) : null)}
                      readOnly={effortConfig.auto_update_estimated_hours && !effortConfig.allow_manual_override}
                      className={`text-lg font-bold bg-transparent border-none focus:ring-0 w-14 p-0 ${isDark ? 'text-blue-400' : 'text-blue-600'} ${effortConfig.auto_update_estimated_hours && !effortConfig.allow_manual_override ? 'cursor-default opacity-70' : ''}`}
                      placeholder={showEstimatedResults ? '0' : '-'}
                    />
                  ) : (
                    <span className={`text-lg font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>-</span>
                  )}
                  <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{effortConfig.hour_label}</span>
                </div>
                <div className={`text-[11px] mt-1 min-h-[1rem] ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {!showEstimatedResults
                    ? 'Select a complexity value to calculate the estimate.'
                    : effortConfig.calculation_mode === 'manual'
                      ? 'Manual entry only'
                      : effortConfig.calculation_mode === 'schedule_based'
                        ? 'Calculated from schedule duration'
                        : effortConfig.calculation_mode === 'hybrid'
                          ? `Hybrid: ${Math.round(effortConfig.schedule_weight_in_hybrid * 100)}% schedule, ${Math.round((1 - effortConfig.schedule_weight_in_hybrid) * 100)}% points`
                          : selectedEffortScale
                            ? `${selectedEffortScale.label} · ${selectedEffortScale.hours} ${effortConfig.hour_label}`
                            : effortConfig.auto_update_estimated_hours
                              ? 'Estimated hours follow the selected complexity'
                              : 'Manual estimate enabled'}
                </div>
              </div>
              {/* Actual */}
              <div className={`p-2.5 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-green-50'} border ${isDark ? 'border-gray-700' : 'border-green-100'} relative`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <PlayCircle className={`w-3.5 h-3.5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Actual</span>
                  {timeEntriesTotal !== null && timeEntriesTotal > 0 && (
                    <span className={`text-xs ${isDark ? 'text-green-500' : 'text-green-600'}`} title={timeEntriesSource === 'appointments' ? 'Sum of child appointment time entries' : 'From appointment time entries'}>
                      ⏱
                    </span>
                  )}
                </div>
                {timeEntriesTotal !== null && timeEntriesTotal > 0 ? (
                  <>
                    <div className={`text-lg font-bold ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                      {timeEntriesTotal.toFixed(2)} hrs
                    </div>
                    <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      {workItem.item_type === 'job' && childAppointmentCount > 0
                        ? `From ${childAppointmentCount} child appointment${childAppointmentCount !== 1 ? 's' : ''}`
                        : workItem.item_type === 'appointment'
                          ? 'From appointment time entries'
                          : 'From time entries'}
                    </div>
                  </>
                ) : (
                  <>
                    <div className={`text-lg font-bold ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>0 hrs</div>
                    <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                      No time entries yet
                    </div>
                  </>
                )}
              </div>
              {/* Surplus / Shortfall */}
              <div className={`p-2.5 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-amber-50'} border ${isDark ? 'border-gray-700' : 'border-amber-100'}`}>
                <div className="flex items-center gap-1.5 mb-1">
                  <TrendingUp className={`w-3.5 h-3.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                  <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Surplus / Shortfall</span>
                </div>
                {(() => {
                  if (effectiveEstimatedHours && actualHours > 0) {
                    const diff = effectiveEstimatedHours - actualHours;
                    const diffAbs = Math.abs(diff);
                    const isSurplus = diff >= 0;
                    const efficiency = Math.round((effectiveEstimatedHours / actualHours) * 100);
                    return (
                      <div>
                        <div className={`text-lg font-bold ${isSurplus ? isDark ? 'text-green-400' : 'text-green-600' : isDark ? 'text-red-400' : 'text-red-600'}`}>
                          {isSurplus ? '+' : '-'}{diffAbs.toFixed(1)}h
                        </div>
                        <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          {isSurplus ? 'Under budget' : 'Over budget'} ({efficiency}%)
                        </div>
                      </div>
                    );
                  }
                  return <div className={`text-lg font-bold ${isDark ? 'text-gray-600' : 'text-gray-300'}`}>—</div>;
                })()}
              </div>
              {/* Travel Time */}
              {travelMinutes > 0 && (
                <div className={`p-2.5 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-purple-50'} border ${isDark ? 'border-gray-700' : 'border-purple-100'}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <Navigation className={`w-3.5 h-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Travel</span>
                  </div>
                  <div className={`text-lg font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                    {travelMinutes >= 60 ? `${(travelMinutes / 60).toFixed(1)}h` : `${travelMinutes}m`}
                  </div>
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    En route time
                  </div>
                </div>
              )}
              {/* Travel Distance */}
              {totalDistanceKm > 0 && (
                <div className={`p-2.5 rounded-lg ${isDark ? 'bg-gray-900/50' : 'bg-indigo-50'} border ${isDark ? 'border-gray-700' : 'border-indigo-100'}`}>
                  <div className="flex items-center gap-1.5 mb-1">
                    <MapPin className={`w-3.5 h-3.5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Distance</span>
                  </div>
                  <div className={`text-lg font-bold ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`}>
                    {totalDistanceKm.toFixed(1)} km
                  </div>
                  <div className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Travel distance
                  </div>
                </div>
              )}
            </div>
            {/* Full meaning for the selected complexity (shown in full below the stats row) */}
            <div
              className={`rounded-lg border px-3 py-2.5 ${isDark ? 'border-gray-700 bg-gray-900/40' : 'border-purple-100 bg-purple-50/80'}`}
            >
              {selectedEffortScale ? (
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  {selectedEffortScale.description?.trim()
                    ? selectedEffortScale.description.trim()
                    : selectedEffortScale.label}
                </p>
              ) : (
                <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  Choose a complexity level to see the full description of that effort band.
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Compact Progress Bar */}
        <div className="mt-3">
          <div className={`relative h-2 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
            <div className={`absolute inset-y-0 left-0 rounded-full transition-all duration-500 ${displayProgressPercentage === 100 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
              displayProgressPercentage >= 75 ? 'bg-gradient-to-r from-blue-500 to-cyan-500' :
                displayProgressPercentage >= 50 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                  displayProgressPercentage >= 25 ? 'bg-gradient-to-r from-orange-500 to-amber-500' :
                    'bg-gradient-to-r from-red-500 to-orange-500'
              }`} style={{ width: `${displayProgressPercentage}%` }} />
          </div>
          <div className="flex justify-between mt-1 text-xs gap-3">
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Start</span>
            <span className={`text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{progressSummary.description}</span>
            <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Done</span>
          </div>
        </div>
      </div>

      {/* Appointment-Specific: Parent Job Reference & Schedule */}
      {workItem.item_type === 'appointment' && (
        <>
          {/* Customer Info + Schedule side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Customer Info (from parent job) */}
            {workItem.contact_name && (
              <div className={`p-3 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                  <Building className="w-4 h-4" />
                  Job Customer
                </h3>
                <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                  <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {workItem.contact_name}
                  </div>
                  {workItem.contact_email && (
                    <div className={`flex items-center gap-1.5 text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <Mail className="w-3 h-3" /> {workItem.contact_email}
                    </div>
                  )}
                  {workItem.contact_phone && (
                    <div className={`flex items-center gap-1.5 text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      <Phone className="w-3 h-3" /> {workItem.contact_phone}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Appointment Schedule */}
            <div className={`p-3 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                <Calendar className="w-4 h-4 text-blue-500" />
                Schedule
              </h3>
              {/* Formatted date display */}
              {formData.scheduled_start && formData.scheduled_end && (
                <div className={`mb-3 text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                  {(() => {
                    const startFormatted = formatDateTimeSAST(formData.scheduled_start);
                    const endTimeFormatted = formatTimeSAST(formData.scheduled_end);
                    return `${startFormatted} – ${endTimeFormatted}`;
                  })()}
                </div>
              )}
              <AppointmentScheduler
                scheduledStart={scheduledStartInputValue || getDefaultAppointmentSchedule().scheduled_start}
                scheduledEnd={scheduledEndInputValue || getDefaultAppointmentSchedule().scheduled_end}
                onChange={({ scheduled_start, scheduled_end }) => {
                  const normalized = normalizeAppointmentSchedule(scheduled_start, scheduled_end, {
                    defaultDurationMinutes: DEFAULT_APPOINTMENT_DURATION_MINUTES,
                  });

                  const startDate = normalized.scheduled_start && normalized.scheduled_start.trim() ? new Date(normalized.scheduled_start) : null;
                  const endDate = normalized.scheduled_end && normalized.scheduled_end.trim() ? new Date(normalized.scheduled_end) : null;

                  onDataChange('scheduled_start', startDate && !Number.isNaN(startDate.getTime()) ? startDate.toISOString() : null);
                  onDataChange('scheduled_end', endDate && !Number.isNaN(endDate.getTime()) ? endDate.toISOString() : null);
                }}
                isDark={isDark}
                className={isDark ? 'bg-gray-900/40 border-gray-700' : 'bg-blue-50/50 border-blue-200'}
              />
              {/* Duration display */}
              {formData.scheduled_start && formData.scheduled_end && (
                <div className={`mt-2 flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <Clock className="w-3 h-3" />
                  Duration: {(() => {
                    const start = new Date(formData.scheduled_start);
                    const end = new Date(formData.scheduled_end);
                    const diffMs = end.getTime() - start.getTime();
                    const hours = Math.floor(diffMs / (1000 * 60 * 60));
                    const mins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
                    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
                  })()}
                </div>
              )}
              {appointmentScheduleError && (
                <div className={`mt-2 text-xs ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                  {appointmentScheduleError}
                </div>
              )}
            </div>
          </div>

          {workItem.item_type === 'appointment' && supersededByFollowUpDisplay && (
            <div
              className={`rounded-xl border p-4 ${isDark ? 'border-emerald-800 bg-emerald-950/25' : 'border-emerald-200 bg-emerald-50/90'
                }`}
            >
              <p className={`text-sm ${isDark ? 'text-emerald-100' : 'text-emerald-900'}`}>
                <span className="font-semibold">Superseded.</span>{' '}
                {workItem.item_number ? (
                  <>
                    Appointment <span className="font-mono">{workItem.item_number}</span>
                  </>
                ) : (
                  'This visit'
                )}{' '}
                was resolved and replaced with new appointment{' '}
                <span className="font-mono font-semibold">{supersededByFollowUpDisplay}</span>
                {followUpSupersededNotice ? ' (just now)' : ''}.
              </p>
            </div>
          )}

          {showFollowUpNewAppointmentButton && (
            <div
              className={`rounded-xl border p-4 ${isDark ? 'border-blue-800 bg-blue-950/30' : 'border-blue-200 bg-blue-50/80'
                }`}
            >
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-blue-100' : 'text-blue-900'}`}>
                    <Plus className="w-4 h-4 shrink-0" />
                    Follow-up visit on the same job
                  </h3>
                  <p className={`mt-1 text-xs ${isDark ? 'text-blue-200/80' : 'text-blue-800/90'}`}>
                    This visit is {followUpResolutionContextPhrase}. Creating a new appointment resolves this one automatically and records
                    that it was superseded by the new appointment number (for example APT352). The new visit uses the same parent job and
                    copies contact and site details from this appointment.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => { void handleCreateFollowUpAppointment(); }}
                  disabled={followUpAppointmentSubmitting}
                  className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity shrink-0 ${followUpAppointmentSubmitting ? 'opacity-60 cursor-not-allowed' : 'hover:opacity-95'
                    }`}
                  style={{ backgroundColor: '#2563eb' }}
                >
                  {followUpAppointmentSubmitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                  New appointment
                </button>
              </div>
            </div>
          )}

          {showAppointmentCompletionOverrideCard && (
            <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className={`text-sm font-semibold flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    <ShieldAlert className="w-4 h-4 text-amber-500" />
                    Completion override
                  </h3>
                  <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    When enabled, SmartTask skips appointment completion gates for this appointment and pushes the change live to the assignees.
                  </p>
                </div>

                <label className={`inline-flex items-center ${canManageAppointmentCompletionOverride ? 'cursor-pointer' : 'cursor-not-allowed opacity-70'}`}>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={appointmentCompletionOverrideForm.enabled}
                    disabled={!canManageAppointmentCompletionOverride || savingAppointmentCompletionOverride}
                    onChange={(e) => setAppointmentCompletionOverrideForm((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))}
                  />
                  <span className={`relative h-6 w-11 rounded-full transition-colors ${appointmentCompletionOverrideForm.enabled ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'}`}>
                    <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${appointmentCompletionOverrideForm.enabled ? 'translate-x-5' : 'translate-x-0'}`} />
                  </span>
                </label>
              </div>

              {appointmentCompletionOverrideForm.enabled && (
                <div className="mt-3">
                  <label className={`block text-[11px] font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={3}
                    value={appointmentCompletionOverrideForm.reason}
                    disabled={!canManageAppointmentCompletionOverride || savingAppointmentCompletionOverride}
                    onChange={(e) => setAppointmentCompletionOverrideForm((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))}
                    placeholder="Explain why this appointment may bypass the normal completion gates"
                    className={`w-full px-3 py-2 text-sm rounded-lg border resize-none ${isDark ? 'bg-gray-900 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-800'}`}
                  />
                  <p className={`mt-1 text-[11px] ${isAppointmentCompletionOverrideReasonValid ? (isDark ? 'text-gray-400' : 'text-gray-500') : 'text-amber-600 dark:text-amber-400'}`}>
                    {isAppointmentCompletionOverrideReasonValid
                      ? 'This reason is stored with the override and included in the live event payload.'
                      : `Please enter at least ${APPOINTMENT_COMPLETION_OVERRIDE_REASON_MIN_LENGTH} characters before saving.`}
                  </p>
                </div>
              )}

              {!canManageAppointmentCompletionOverride && persistedAppointmentCompletionOverride.enabled && (
                <p className={`mt-3 text-[11px] ${isDark ? 'text-amber-300' : 'text-amber-700'}`}>
                  This override is active and can only be changed by an authorized admin user.
                </p>
              )}

              {persistedAppointmentCompletionOverride.enabled && persistedAppointmentCompletionOverride.approvedAt && (
                <p className={`mt-3 text-[11px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  Last approved by {persistedAppointmentCompletionOverride.approvedByName || 'an authorized user'} on {formatDate(persistedAppointmentCompletionOverride.approvedAt)}.
                </p>
              )}

              {canManageAppointmentCompletionOverride && (
                <div className="mt-3 flex justify-end">
                  <button
                    type="button"
                    onClick={() => {
                      if (appointmentCompletionOverrideAction === 'remove') {
                        void persistAppointmentCompletionOverride(false);
                        return;
                      }

                      if (appointmentCompletionOverrideAction === 'none') {
                        setAppointmentCompletionOverrideForm((prev) => ({ ...prev, enabled: true }));
                        return;
                      }

                      void persistAppointmentCompletionOverride(true);
                    }}
                    disabled={savingAppointmentCompletionOverride || !canSubmitAppointmentCompletionOverrideAction}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {savingAppointmentCompletionOverride ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
                    {appointmentCompletionOverrideAction === 'remove'
                      ? 'Remove override'
                      : appointmentCompletionOverrideAction === 'update'
                        ? 'Update override'
                        : appointmentCompletionOverrideAction === 'create'
                          ? 'Create override'
                          : 'Enable override'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Live Travel Map — shown when appointment is actively on route */}
          {workItem.item_type === 'appointment' && ['on_route', 'traveling', 'en_route'].includes(String(workItem.status || '').toLowerCase()) && (
            <AppointmentLiveMap workItem={workItem} />
          )}

        </>
      )}

      {/* Asset Equipment Details - Only for item_type='asset' */}
      {workItem.item_type === 'asset' && (
        <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <h3 className={`text-base font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <Wrench className="w-4 h-4 text-orange-500" />
            Equipment Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Serial Number */}
            <div>
              <label className={`text-xs font-medium mb-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Hash className="w-3 h-3" /> Serial Number
              </label>
              <input
                type="text"
                value={formData.custom_fields?.serial_number || ''}
                onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, serial_number: e.target.value })}
                placeholder="Enter serial number"
                className={`w-full px-3 py-2 rounded-lg border text-sm font-mono ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>

            {/* Manufacturer */}
            <div>
              <label className={`text-xs font-medium mb-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Factory className="w-3 h-3" /> Manufacturer
              </label>
              <input
                type="text"
                value={formData.custom_fields?.manufacturer || ''}
                onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, manufacturer: e.target.value })}
                placeholder="e.g. Carrier, Honeywell"
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>

            {/* Model */}
            <div>
              <label className={`text-xs font-medium mb-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Tag className="w-3 h-3" /> Model
              </label>
              <input
                type="text"
                value={formData.custom_fields?.model || ''}
                onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, model: e.target.value })}
                placeholder="Model number or name"
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>

            {/* Install Date */}
            <div>
              <label className={`text-xs font-medium mb-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Calendar className="w-3 h-3" /> Install / Commission Date
              </label>
              <input
                type="date"
                value={formData.custom_fields?.install_date || ''}
                onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, install_date: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>

            {/* Warranty Expiry */}
            <div>
              <label className={`text-xs font-medium mb-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Shield className="w-3 h-3" /> Warranty Expiry
              </label>
              <input
                type="date"
                value={formData.custom_fields?.warranty_expiry || ''}
                onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, warranty_expiry: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>

            {/* Next Service Due */}
            <div>
              <label className={`text-xs font-medium mb-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Wrench className="w-3 h-3" /> Next Service Due
              </label>
              <input
                type="date"
                value={formData.custom_fields?.next_service_date || ''}
                onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, next_service_date: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
              {formData.custom_fields?.next_service_date && (() => {
                const days = Math.ceil((new Date(formData.custom_fields.next_service_date).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                if (days < 0) return <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> {Math.abs(days)} days overdue</p>;
                if (days <= 30) return <p className="text-xs text-yellow-500 mt-1">{days} days remaining</p>;
                return <p className="text-xs text-green-500 mt-1">{days} days remaining</p>;
              })()}
            </div>

            {/* Service Interval */}
            <div>
              <label className={`text-xs font-medium mb-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <CalendarClock className="w-3 h-3" /> Service Interval (days)
              </label>
              <input
                type="number"
                min="1"
                value={formData.custom_fields?.service_interval_days || ''}
                onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, service_interval_days: parseInt(e.target.value) || '' })}
                placeholder="e.g. 365"
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>

            {/* Service Provider */}
            <div>
              <label className={`text-xs font-medium mb-1 flex items-center gap-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <User className="w-3 h-3" /> Service Provider
              </label>
              <input
                type="text"
                value={formData.custom_fields?.service_provider || ''}
                onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, service_provider: e.target.value })}
                placeholder="Name of service company"
                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              />
            </div>
          </div>

          {/* Compliance / Certification Section - for H&S assets */}
          <div className="mt-4 pt-4 border-t" style={{ borderColor: isDark ? '#374151' : '#e5e7eb' }}>
            <h4 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
              <ShieldCheck className="w-4 h-4 text-green-500" />
              Compliance & Certification
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Certificate Number */}
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Certificate Number</label>
                <input
                  type="text"
                  value={formData.custom_fields?.certificate_number || ''}
                  onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, certificate_number: e.target.value })}
                  placeholder="Certificate or compliance number"
                  className={`w-full px-3 py-2 rounded-lg border text-sm font-mono ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              {/* Certificate Expiry */}
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Certificate Expiry</label>
                <input
                  type="date"
                  value={formData.custom_fields?.certificate_expiry || ''}
                  onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, certificate_expiry: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
                {formData.custom_fields?.certificate_expiry && (() => {
                  const days = Math.ceil((new Date(formData.custom_fields.certificate_expiry).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
                  if (days < 0) return <p className="text-xs text-red-500 mt-1 flex items-center gap-1"><ShieldAlert className="w-3 h-3" /> Certificate expired {Math.abs(days)} days ago</p>;
                  if (days <= 30) return <p className="text-xs text-yellow-500 mt-1">Expiring in {days} days</p>;
                  return <p className="text-xs text-green-500 mt-1">Valid for {days} days</p>;
                })()}
              </div>

              {/* Regulatory Standard */}
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Regulatory Standard</label>
                <input
                  type="text"
                  value={formData.custom_fields?.regulatory_standard || ''}
                  onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, regulatory_standard: e.target.value })}
                  placeholder="e.g. SANS 1475, SANS 10400"
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>

              {/* Last Inspection Outcome */}
              <div>
                <label className={`text-xs font-medium mb-1 block ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Last Inspection Outcome</label>
                <select
                  value={formData.custom_fields?.last_inspection_outcome || ''}
                  onChange={(e) => onDataChange('custom_fields', { ...formData.custom_fields, last_inspection_outcome: e.target.value })}
                  className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="">Not inspected</option>
                  <option value="pass">Pass</option>
                  <option value="conditional">Conditional Pass</option>
                  <option value="fail">Fail</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Basic Info + Contact Section - Combined Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Basic Information - Compact, spans 2 rows for appointments to cover contact + location sections */}
        <div className={`p-4 rounded-lg ${workItem.item_type === 'appointment' ? 'lg:row-span-2' : ''} ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
          <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            <FileText className="w-4 h-4" />
            Basic Information
          </h3>
          <div className="space-y-3">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Title <span className="text-red-500">*</span>
              </label>
              <input type="text" value={formData.title || ''} onChange={(e) => onDataChange('title', e.target.value)}
                className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="Enter title" />
            </div>
            {/* Dynamic subtype selector for jobs and appointments */}
            {(workItem.item_type === 'job' || workItem.item_type === 'appointment') && availableSubtypeOptions.length > 0 && (
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Briefcase className="w-3 h-3 inline mr-1" />
                  {workItem.item_type === 'appointment' ? 'Appointment Type' : 'Job Type'}
                </label>
                <select
                  value={workItem.item_type === 'appointment' ? normalizeAppointmentTypeKey(formData.item_subtype || '') : formData.item_subtype || ''}
                  onChange={(e) => {
                    const nextSubtypeValue = e.target.value || null;
                    if (workItem.item_type === 'appointment') {
                      void handleSubtypeChange(nextSubtypeValue);
                      return;
                    }

                    onDataChange('item_subtype', nextSubtypeValue);
                  }}
                  className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="">{workItem.item_type === 'appointment' ? 'No specific appointment type' : 'No specific type'}</option>
                  {availableSubtypeOptions.map(type => (
                    <option key={type.key} value={type.key}>{type.label} {type.visible_to_customer !== false ? '\u{1F7E2} Visible to customer' : '\u{1F534} Not visible to customer'}</option>
                  ))}
                </select>
              </div>
            )}
            {/* Show read-only type badge when the item type has no editable subtype selector */}
            {workItem.item_type !== 'job' && workItem.item_type !== 'appointment' && workItem.item_subtype && (
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Briefcase className="w-3 h-3 inline mr-1" />
                  Type
                </label>
                <span className={`inline-block px-2.5 py-1 text-xs font-medium rounded-lg ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'}`}>
                  {workItem.item_subtype.replace(/[_-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
              </div>
            )}
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
              <textarea value={formData.description || ''} onChange={(e) => onDataChange('description', e.target.value)} rows={6}
                className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 resize-y min-h-[120px] ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                placeholder="Brief description" />
            </div>
            {(workItem.item_type === 'job' || workItem.item_type === 'appointment') && !isPurchaseAppointment && (
              <div>
                <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Additional Notes</label>
                <textarea
                  value={formData.notes ?? workItem.notes ?? ''}
                  onChange={(e) => onDataChange('notes', e.target.value)}
                  rows={4}
                  className={`w-full px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 resize-y min-h-[96px] ${isDark ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Additional notes or special instructions"
                />
              </div>
            )}

            {isPurchaseAppointment && (
              <PurchaseItemList
                items={purchaseItems}
                onChange={handlePurchaseItemsChange}
                isDark={isDark}
                mode="edit"
                title="Itemized Purchase List"
                helperText="For purchase appointments, capture the buy list here instead of generic notes so the field team and AI review can work from the same checklist."
              />
            )}
          </div>
        </div>

        {/* Contact/Location/Person - Compact (hidden for appointments — use Appointment Location instead) */}
        {workItem.item_type !== 'appointment' && (
          <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`} data-tour-id="overview-contact-lookup">
            <UnifiedContactLookup
              contacts={sharedContacts}
              locations={sharedLocations}
              contactPeople={sharedContactPeople}
              selectedContactId={resolvedContactId != null ? Number(resolvedContactId) : null}
              selectedContactWorkItemId={selectedContactWorkItemId}
              selectedLocationId={resolvedLocationId != null ? Number(resolvedLocationId) : null}
              selectedLocationWorkItemId={selectedLocationWorkItemId}
              selectedContactPersonId={resolvedContactPersonId != null ? Number(resolvedContactPersonId) : null}
              selectedContactPersonWorkItemId={selectedContactPersonWorkItemId}
              selectedContactPersonName={selectedPerson ? `${selectedPerson.first_name} ${selectedPerson.last_name}`.trim() : resolvedContactPersonName}
              maxContactPeople={3}
              selectedContactPersonIds={[
                resolvedContactPersonId,
                resolvedContactPersonId2,
                resolvedContactPersonId3,
              ].filter((id): id is number => id != null)}
              onContactChange={handleContactChange}
              onLocationChange={handleLocationChange}
              onContactPersonChange={handlePersonChange}
              onContactPersonsChange={handlePersonsChange}
              onUnifiedSelect={handleUnifiedSelect}
              onLocationCreated={handleSharedLocationCreated}
              onPersonCreated={handleSharedPersonCreated}
              loadingContacts={loadingContacts}
              loadingLocations={loadingLocations}
              loadingPeople={loadingPeople}
              isDark={isDark}
              contactTypes={['customer']}
              onViewStakeholder={selectedContactWorkItemId && onOpenItem ? () => onOpenItem(selectedContactWorkItemId) : undefined}
              onEditContact={onOpenItem ? (contact) => onOpenItem(contact.work_item_id || buildStakeholderEntityWorkItemId('contact', contact.id)) : undefined}
            />

            {/* Location Verification Settings - Only for jobs and appointments */}
            {(workItem.item_type === 'job' || workItem.item_type === 'appointment') && (
              <div className={`mt-2 p-2.5 rounded-lg border ${isDark ? 'bg-blue-900/20 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                {/* Require Exact Location Toggle */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={!!formData.require_exact_location}
                      onChange={(e) => onDataChange('require_exact_location', e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${formData.require_exact_location
                      ? 'bg-blue-600'
                      : isDark ? 'bg-gray-600' : 'bg-gray-300'
                      }`}>
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${formData.require_exact_location ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                    </div>
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      <Target className="w-3 h-3 inline mr-1" />
                      Require Exact Location
                    </span>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Worker must be at {workItem.item_type === 'appointment' ? 'appointment' : 'job'} site to check in
                    </p>
                  </div>
                </label>

                {/* Geofence Radius */}
                {formData.require_exact_location && (
                  <div className="mt-3">
                    <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <MapPin className="w-3 h-3 inline mr-1" />
                      Acceptable Distance (meters)
                    </label>
                    <select
                      value={formData.geofence_radius_meters || 100}
                      onChange={(e) => onDataChange('geofence_radius_meters', parseInt(e.target.value))}
                      className={`w-full text-sm px-2 py-1.5 rounded-lg border ${isDark ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-700'}`}
                    >
                      <option value={50}>50m - Very precise</option>
                      <option value={100}>100m - Standard</option>
                      <option value={200}>200m - Relaxed</option>
                      <option value={500}>500m - Large area</option>
                    </select>
                    <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Worker must be within this distance to check in
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Parent job resolution toggle - Only for appointments */}
            {workItem.item_type === 'appointment' && (
              <div className={`mt-3 p-3 rounded-lg border ${isDark ? 'bg-emerald-900/20 border-emerald-800' : 'bg-emerald-50 border-emerald-200'}`}>
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={resolveParentJobOnResolve}
                      onChange={(e) => handleResolveParentJobOnResolveChange(e.target.checked)}
                      className="sr-only"
                    />
                    <div className={`w-11 h-6 rounded-full transition-colors ${resolveParentJobOnResolve
                      ? 'bg-emerald-600'
                      : isDark ? 'bg-gray-600' : 'bg-gray-300'
                      }`}>
                      <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${resolveParentJobOnResolve ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                    </div>
                  </div>
                  <div>
                    <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
                      <ArrowRight className="w-3 h-3 inline mr-1" />
                      Resolve job when resolving this appointment
                    </span>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      When enabled, Smart Task will also try to resolve the parent job. If the parent job still has unmet process checklist requirements, this appointment will stay open too.
                    </p>
                  </div>
                </label>
              </div>
            )}
          </div>
        )}

        {/* Appointment Location (appointments only — replaces the standard Contact/Location/Person section) */}
        {workItem.item_type === 'appointment' && (
          <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
            <h3 className={`text-sm font-semibold mb-3 flex items-center gap-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              <MapPin className="w-4 h-4 text-green-500" />
              Appointment Location
            </h3>

            {hasAppointmentContactSummary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <div className={`p-3 rounded-lg border ${selectedContact
                  ? isDark ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'
                  : isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed'
                  }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${selectedContact ? (isDark ? 'bg-blue-600' : 'bg-blue-500') : isDark ? 'bg-gray-700' : 'bg-gray-200'
                      }`}>
                      <Building2 className={`w-4 h-4 ${selectedContact ? 'text-white' : 'text-gray-400'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contact</div>
                      {isEditingAppointmentContact ? (
                        <select
                          value={resolvedContactId || ''}
                          onChange={(e) => {
                            const contactId = e.target.value ? parseInt(e.target.value) : null;
                            const contact = contacts.find((c) => c.id === contactId);
                            if (contactId && contact) {
                              handleContactChange(contactId, contact.company_name || contact.display_name || contact.contact_person || undefined);
                              setLocationSource('contact');
                              setCustomLocation(null);
                              setCustomLocSearch('');
                            }
                            setIsEditingAppointmentContact(false);
                          }}
                          className={`w-full mt-1 text-sm rounded border px-2 py-1 ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                          autoFocus
                        >
                          <option value="">Select contact...</option>
                          {contacts.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.company_name || c.display_name || c.contact_person || `Contact #${c.id}`}
                              {c.contact_type ? ` (${c.contact_type})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <div className={`text-sm font-semibold truncate ${selectedContact ? (isDark ? 'text-gray-200' : 'text-gray-800') : 'text-gray-400'}`}>
                          {resolvedCompanyName || 'Not selected'}
                        </div>
                      )}
                    </div>
                    {!isEditingAppointmentContact && (
                      <button
                        type="button"
                        onClick={() => setIsEditingAppointmentContact(true)}
                        className={`p-1.5 rounded-md transition-colors ${isDark ? 'hover:bg-blue-800/50 text-blue-400' : 'hover:bg-blue-100 text-blue-600'}`}
                        title="Change contact"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {selectedContact?.email && !isEditingAppointmentContact && (
                    <div className={`text-xs truncate ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{selectedContact.email}</div>
                  )}
                </div>

                <div className={`p-3 rounded-lg border ${resolvedLocationName
                  ? isDark ? 'bg-green-900/20 border-green-700' : 'bg-green-50 border-green-200'
                  : isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed'
                  }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${resolvedLocationName ? (isDark ? 'bg-green-600' : 'bg-green-500') : isDark ? 'bg-gray-700' : 'bg-gray-200'
                      }`}>
                      <MapPin className={`w-4 h-4 ${resolvedLocationName ? 'text-white' : 'text-gray-400'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Location</div>
                      <div className={`text-sm font-semibold truncate ${resolvedLocationName ? (isDark ? 'text-gray-200' : 'text-gray-800') : 'text-gray-400'}`}>
                        {resolvedLocationName || 'Not selected'}
                      </div>
                    </div>
                  </div>
                  {resolvedLocationAddress && (
                    <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{resolvedLocationAddress}</div>
                  )}
                </div>

                <div className={`p-3 rounded-lg border ${resolvedPersonName
                  ? isDark ? 'bg-purple-900/20 border-purple-700' : 'bg-purple-50 border-purple-200'
                  : isDark ? 'bg-gray-800/50 border-gray-700 border-dashed' : 'bg-gray-50 border-gray-300 border-dashed'
                  }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${resolvedPersonName ? (isDark ? 'bg-purple-600' : 'bg-purple-500') : isDark ? 'bg-gray-700' : 'bg-gray-200'
                      }`}>
                      <User className={`w-4 h-4 ${resolvedPersonName ? 'text-white' : 'text-gray-400'}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className={`text-xs font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Contact Person</div>
                      <div className={`text-sm font-semibold truncate ${resolvedPersonName ? (isDark ? 'text-gray-200' : 'text-gray-800') : 'text-gray-400'}`}>
                        {resolvedPersonName || 'Not selected'}
                      </div>
                    </div>
                  </div>
                  {(resolvedPersonRole || resolvedPersonPhone || resolvedPersonEmail) && (
                    <div className={`text-xs space-y-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {resolvedPersonRole && <div className="truncate">{resolvedPersonRole}</div>}
                      {resolvedPersonPhone && <div>{resolvedPersonPhone}</div>}
                      {resolvedPersonEmail && <div className="truncate">{resolvedPersonEmail}</div>}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Mode toggle: Contact Location | Custom */}
            <div className={`flex p-1 rounded-lg mb-3 gap-1 ${isDark ? 'bg-gray-700/60' : 'bg-gray-100'}`}>
              <button
                type="button"
                onClick={() => {
                  userToggledModeRef.current = true;
                  setLocationSource('contact');
                  setCustomLocation(null);
                  setCustomLocSearch('');
                  if (!formData.location_id) {
                    onDataChange('location_name', null);
                    onDataChange('location_address', null);
                    onDataChange('location_coordinates', null);
                  }
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${locationSource === 'contact'
                  ? (isDark ? 'bg-gray-600 text-white shadow-sm' : 'bg-white text-gray-800 shadow-sm')
                  : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                  }`}
              >
                <Building2 className="w-3.5 h-3.5" />
                Contact Location
              </button>
              <button
                type="button"
                onClick={() => {
                  userToggledModeRef.current = true;
                  setLocationSource('custom');
                  onDataChange('location_id', null);
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${locationSource === 'custom'
                  ? (isDark ? 'bg-orange-600 text-white shadow-sm' : 'bg-orange-500 text-white shadow-sm')
                  : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700')
                  }`}
              >
                <MapPin className="w-3.5 h-3.5" />
                Custom
              </button>
            </div>

            {/* CONTACT LOCATION MODE */}
            {locationSource === 'contact' && (
              <>
                {loadingLocations ? (
                  <div className={`w-full px-3 py-2 border rounded-lg flex items-center gap-2 text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-300 text-gray-500'}`}>
                    <Loader2 className="w-4 h-4 animate-spin" /> Loading locations...
                  </div>
                ) : effectiveLocations.length === 0 && resolvedContactId ? (
                  <div className={`w-full px-3 py-2 border rounded-lg text-sm flex items-center justify-between gap-2 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-300 text-gray-500'}`}>
                    <span>No locations on file for this contact</span>
                    <button type="button" onClick={() => { userToggledModeRef.current = true; setLocationSource('custom'); }}
                      className="text-xs text-orange-500 hover:text-orange-600 font-medium whitespace-nowrap flex items-center gap-1">
                      <MapPin className="w-3 h-3" />Use custom instead
                    </button>
                  </div>
                ) : !resolvedContactId ? (
                  <div className={`w-full px-3 py-2 border rounded-lg text-sm flex items-center justify-between gap-2 ${isDark ? 'bg-gray-700 border-gray-600 text-gray-400' : 'bg-gray-50 border-gray-300 text-gray-500'}`}>
                    <span>Select a contact first, or use custom location</span>
                    <button type="button" onClick={() => { userToggledModeRef.current = true; setLocationSource('custom'); }}
                      className="text-xs text-orange-500 hover:text-orange-600 font-medium whitespace-nowrap flex items-center gap-1">
                      <MapPin className="w-3 h-3" />Use custom
                    </button>
                  </div>
                ) : (
                  <select
                    value={resolvedLocationId || ''}
                    onChange={(e) => handleLocationChange(e.target.value ? parseInt(e.target.value) : null)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  >
                    <option value="">Select location...</option>
                    {effectiveLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.location_name}{loc.is_primary ? ' ★' : ''}{loc.address_line1 ? ` — ${loc.address_line1}` : ''}{loc.city ? `, ${loc.city}` : ''}
                      </option>
                    ))}
                  </select>
                )}
                {selectedLocation && (
                  <div className={`mt-2 p-3 rounded-lg border ${isDark ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200'}`}>
                    <div className="flex items-start gap-2">
                      <MapPin className={`w-4 h-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                      <div>
                        <div className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {selectedLocation.location_name}
                        </div>
                        {selectedLocation.address_line1 && (
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {selectedLocation.address_line1}{selectedLocation.city ? `, ${selectedLocation.city}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* CUSTOM LOCATION MODE */}
            {locationSource === 'custom' && (
              <div className="space-y-3">
                <div className={`flex items-start gap-2 px-3 py-2 rounded-lg text-xs ${isDark ? 'bg-orange-900/20 border border-orange-700/50 text-orange-300' : 'bg-orange-50 border border-orange-200 text-orange-700'
                  }`}>
                  <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                  <span>This location is stored only on this appointment and is <strong>not</strong> linked to the contact&apos;s locations.</span>
                </div>

                <LocationAutocomplete
                  value={customLocSearch}
                  onChange={setCustomLocSearch}
                  onPlaceSelect={(data) => {
                    setCustomLocation(data);
                    setCustomLocSearch(data.name);
                    onDataChange('location_name', data.name);
                    onDataChange('location_address', data.address);
                    onDataChange('location_coordinates', data.lat && data.lng ? { lat: parseFloat(data.lat), lng: parseFloat(data.lng) } : null);
                    onDataChange('location_id', null);
                  }}
                  onClear={() => {
                    setCustomLocSearch('');
                    setCustomLocation(null);
                    onDataChange('location_name', null);
                    onDataChange('location_address', null);
                    onDataChange('location_coordinates', null);
                  }}
                  isDark={isDark}
                />

                {customLocation && (
                  <CustomLocationResult
                    location={customLocation}
                    onClear={() => {
                      setCustomLocation(null);
                      setCustomLocSearch('');
                      onDataChange('location_name', null);
                      onDataChange('location_address', null);
                      onDataChange('location_coordinates', null);
                    }}
                    isDark={isDark}
                  />
                )}

                {!customLocation && (
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    Start typing to search — select a result from the map search dropdown to capture coordinates.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── SUBTASKS SECTION ─── */}
      {showSubtasksSection && (
        <div className={`rounded-xl border ${isDark ? 'bg-gray-800/50 border-gray-700/50' : 'bg-white border-gray-200'}`}>
          {/* Section Header */}
          <div className={`flex items-center justify-between px-4 py-3 border-b ${isDark ? 'border-gray-700/50' : 'border-gray-100'}`}>
            <button
              onClick={() => setSubtasksExpanded(!subtasksExpanded)}
              className="flex items-center gap-2 group"
            >
              <ChevronRight className={`w-4 h-4 transition-transform ${subtasksExpanded ? 'rotate-90' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Subtasks
              </h3>
              {subtasks.length > 0 && (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${completedSubtasks === subtasks.length && subtasks.length > 0
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                  : isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                  }`}>
                  {completedSubtasks}/{subtasks.length}
                </span>
              )}
            </button>
            <div className="flex items-center gap-1.5">
              {/* Quick Add Toggle */}
              <button
                onClick={() => { setShowQuickAdd(!showQuickAdd); }}
                className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-all ${showQuickAdd
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : isDark
                    ? 'bg-gray-700 text-gray-200 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                title="Quick add subtask"
              >
                <Plus className="w-3.5 h-3.5" />
                Add
              </button>
            </div>
          </div>

          {/* Expanding content */}
          {subtasksExpanded && (
            <div className="px-4 py-3 space-y-2">
              {/* Progress Bar */}
              {subtasks.length > 0 && (
                <div className="flex items-center gap-2 mb-2">
                  <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
                      style={{ width: `${subtaskProgress}%` }}
                    />
                  </div>
                  <span className={`text-xs font-medium tabular-nums ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    {subtaskProgress}%
                  </span>
                </div>
              )}

              {/* Quick Add Input */}
              {showQuickAdd && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
                  <Circle className={`w-4 h-4 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-300'}`} />
                  <input
                    ref={quickAddInputRef}
                    type="text"
                    value={quickAddTitle}
                    onChange={(e) => setQuickAddTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleQuickAddSubtask();
                      }
                      if (e.key === 'Escape') {
                        setShowQuickAdd(false);
                        setQuickAddTitle('');
                      }
                    }}
                    placeholder="Type subtask title and press Enter..."
                    className={`flex-1 text-sm bg-transparent border-0 outline-none ${isDark ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'}`}
                    disabled={quickAddLoading}
                  />
                  {quickAddLoading && (
                    <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  )}
                </div>
              )}

              {/* Subtask Items with DnD */}
              {loadingSubtasks ? (
                <div className="flex items-center justify-center py-6">
                  <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                  <span className={`ml-2 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Loading subtasks...</span>
                </div>
              ) : subtasks.length === 0 && !showQuickAdd ? (
                <div className={`text-center py-6 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No subtasks yet</p>
                  <p className="text-xs mt-1">Click &quot;Add&quot; to create subtasks</p>
                </div>
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={subtaskIds} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1">
                      {subtasks.map((subtask) => (
                        <SortableSubtask
                          key={subtask.id}
                          subtask={subtask}
                          isDark={isDark}
                          onToggleStatus={() => handleToggleSubtaskStatus(subtask)}
                          onRemove={() => handleRemoveSubtask(subtask)}
                          onOpenSubtask={onOpenSubtask}
                          onOpenItem={onOpenItem}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  </>);
}
